import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Minus, Edit2, Trash2, Printer, FileText, Settings, Eye, Search } from 'lucide-react';
import axios from 'axios';

const LabelPrintingSystem = () => {
  // Utility function to safely format dates
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      return new Date(timestamp).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'N/A';
    }
  };

  const generateA4DualGatePass = async (printJob) => {
    try {
      const labels = (printJob.labels || []).map(l => ({
        roll: l.roll_number || '',
        qty: parseFloat(l.quantity) || 0
      }));
      
      const totalMtr = labels.reduce((sum, l) => sum + l.qty, 0);
      const totalRolls = labels.length;
      const gpNo = getAndIncrementGpNumber();
      const poNo = (typeof window !== 'undefined' ? (window.prompt('Enter P.O.No.', '') || '') : '');

      const labelsPerGP = 70;
      const gpsPerA4Page = 2;
      const totalGPsNeeded = Math.ceil(labels.length / labelsPerGP);
      const totalA4Pages = Math.max(1, Math.ceil(totalGPsNeeded / gpsPerA4Page));

      const buildRollNumberSummary = (gpLabels) => {
        if (!gpLabels || gpLabels.length === 0) return '';
        const sorted = [...gpLabels]
          .map(l => Number(l.roll))
          .filter(n => !isNaN(n))
          .sort((a, b) => a - b);
        if (sorted.length === 0) return '';
        const ranges = [];
        let start = sorted[0];
        let prev = sorted[0];
        for (let i = 1; i < sorted.length; i++) {
          const current = sorted[i];
          if (current === prev + 1) {
            prev = current;
            continue;
          }
          ranges.push(start === prev ? String(start) : `${start}-${prev}`);
          start = current;
          prev = current;
        }
        ranges.push(start === prev ? String(start) : `${start}-${prev}`);
        return ranges.join(', ');
      };

      const buildTableForGP = (gpLabels) => {
        const rowsPerGroup = 10;
        const groupsPerGP = 7;
        
        const groups = Array.from({ length: groupsPerGP }).map((_, groupIndex) => {
          const gStart = groupIndex * rowsPerGroup;
          const gItems = gpLabels.slice(gStart, gStart + rowsPerGroup);
          const groupTotal = gItems.reduce((sum, item) => sum + (item?.qty || 0), 0);
          return { items: gItems, total: groupTotal };
        });

        const headerCells = Array.from({ length: groupsPerGP }).map((_, groupIndex) => {
          const hasDataInGroup = groups[groupIndex].items.some(item => item);
          if (hasDataInGroup) {
            return `<th>S.No</th><th>MTR</th>`;
          }
          return `<th style="visibility: hidden;">S.No</th><th style="visibility: hidden;">MTR</th>`;
        }).join('');

        const filledRows = [];
        for (let rowIndex = 0; rowIndex < rowsPerGroup; rowIndex++) {
          const rowCells = groups.map((group, groupIndex) => {
            const serialNumber = (groupIndex * rowsPerGroup) + rowIndex + 1;
            const item = group.items[rowIndex];
            if (item) {
              return `<td class="serial">${serialNumber}</td><td class="mtr">${item.qty.toFixed(2)}</td>`;
            }
            return `<td class="serial" style="visibility: hidden;">${serialNumber}</td><td class="mtr" style="visibility: hidden;"></td>`;
          }).join('');
          
          const hasData = groups.some(group => group.items[rowIndex]);
          if (hasData) {
            filledRows.push(`<tr>${rowCells}</tr>`);
          }
        }

        const totalsRow = `<tr>
          ${groups.map(g => {
            const hasData = g.items.some(item => item);
            if (hasData) {
              return `<td class="total-label">Total</td><td class="total-value">${g.total > 0 ? g.total.toFixed(2) : ''}</td>`;
            }
            return `<td class="total-label" style="visibility: hidden;">Total</td><td class="total-value" style="visibility: hidden;"></td>`;
          }).join('')}
        </tr>`;

        return `
          <table class="gp-table">
            <thead>
              <tr>${headerCells}</tr>
            </thead>
            <tbody>
              ${filledRows.join('')}
              ${totalsRow}
            </tbody>
          </table>
        `;
      };

      const buildSingleGP = (gpLabels, slipIndex) => {
        if (gpLabels.length === 0) {
          return `
            <div class="gp-slip-placeholder">
              <div class="placeholder-text">No additional items</div>
            </div>
          `;
        }

        const gpMtr = gpLabels.reduce((sum, l) => sum + l.qty, 0);
        const gpRollSummary = buildRollNumberSummary(gpLabels);
        
        const nettWt = (printJob.item && printJob.item.net_weight != null) ? Number(printJob.item.net_weight).toFixed(2) : '';
        const grossWt = (printJob.item && printJob.item.gross_weight != null) ? Number(printJob.item.gross_weight).toFixed(2) : '';
        return `
          <div class="gp-slip">
            <div class="gp-header">
              <div class="header-content">
                <div class="sender-info">
                  <div class="sender-name">SNSM TEXTILES</div>
                  <div class="sender-address">3/297D, Ramaiyan Kadu, P.M. Nagar, Seelanaickenpatti,</div>
                  <div class="sender-address">Salem - 636201, Tamil Nadu.</div>
                  <div class="sender-details">GSTIN: 33AHXPS4027M1ZN</div>
                  <div class="sender-details">PH: +91-9940797757</div>
                </div>
                <div class="document-info">
                  <div class="document-title">Goods Packing Slip</div>
                  <div class="document-meta">No: <strong>${gpNo}-${slipIndex + 1}</strong></div>
                  <div class="document-meta">Date: <strong>${new Date().toLocaleDateString('en-GB')}</strong></div>
                </div>
              </div>
              <div class="header-divider"></div>
            </div>

            <div class="gp-section">
              <div class="customer-line">To, M/s. ${printJob.client.name}, ${printJob.client.location || ''}</div>
              <div class="order-details-section">
                <div class="order-line">
                  <span class="pattern-detail">PTTN# <strong>${printJob.item.patternNumber}</strong> – ${printJob.item.description}</span>
                  <span class="po-detail">PO No: <strong>${poNo || '-'}</strong></span>
                </div>
                <div class="qty-line">Qlty: ${printJob.item.quality || '—'}</div>
              </div>
            </div>

            <div class="gp-content">
              <div class="table-content">
                ${buildTableForGP(gpLabels)}
              </div>
            </div>

            <div class="gp-footer">
              <div class="summary-info">
                <span class="summary-item">Total PCS: <strong>${gpLabels.length}</strong></span>
                <span class="summary-item">Total MTR: <strong>${gpMtr.toFixed(2)}</strong></span>
                <span class="summary-item">Roll Nos: <strong>${gpRollSummary}</strong></span>
                <span class="summary-item">Nett WT: <strong>${nettWt}</strong></span>
                <span class="summary-item">Gross WT: <strong>${grossWt}</strong></span>
              </div>
              <div class="signature-area">
                <div>For SNSM TEXTILES,</div>
                <div class="signature-text"> </div>
              </div>
            </div>
          </div>
        `;
      };

      const buildDualGPPage = (pageIndex) => {
        const firstGPIndex = pageIndex * 2;
        const secondGPIndex = pageIndex * 2 + 1;
        
        const firstGPStart = firstGPIndex * labelsPerGP;
        const firstGPLabels = labels.slice(firstGPStart, firstGPStart + labelsPerGP);
        
        const secondGPStart = secondGPIndex * labelsPerGP;
        const secondGPLabels = labels.slice(secondGPStart, secondGPStart + labelsPerGP);
        
        const firstGP = buildSingleGP(firstGPLabels, firstGPIndex);
        const secondGP = secondGPLabels.length > 0 ? buildSingleGP(secondGPLabels, secondGPIndex) : `
          <div class=\"gp-slip-placeholder\">
            <div class=\"placeholder-text\">No additional items</div>
          </div>
        `;
        
        return `
          <div class=\"dual-gp-container\">
            ${firstGP}
            ${secondGP}
          </div>
        `;
      };

      const allPagesHTML = Array.from({ length: totalA4Pages }).map((_, pageIndex) => 
        buildDualGPPage(pageIndex)
      ).join('');

      const html = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8" />
          <title>Template-Matched A4 Dual GP - ${printJob.client.name}</title>
          <style>
            /* A4 Portrait Dual GP Styles */
            @page {
              size: A4 portrait;
              margin: 10mm 12mm;
            }
            
            body {
              margin: 0;
              padding: 0;
              font-family: Arial, Helvetica, sans-serif;
              font-size: 8pt;
              line-height: 1.1;
              color: #000;
              background: white;
            }
            
            /* Dual GP Container - 2 GPs per A4 page */
            .dual-gp-container {
              width: 186mm;
              min-height: 277mm;
              display: flex;
              flex-direction: column;
              gap: 10mm;
              page-break-after: always;
              page-break-inside: avoid;
              margin: 0 auto;
            }
            
            .dual-gp-container:last-child {
              page-break-after: avoid;
            }
            
            /* Individual GP Slip - Fits in 133.5mm height */
            .gp-slip {
              width: 100%;
              height: 133.5mm;
              border: 1.5px solid #000;
              padding: 4mm;
              box-sizing: border-box;
              display: flex;
              flex-direction: column;
              background: white;
              position: relative;
            }
            
            /* GP Header - Exact Match */
            .gp-header {
              height: 25mm;
              padding: 2mm 0;
              flex-shrink: 0;
              position: relative;
            }
            
            .header-content {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              height: auto;
              margin-bottom: 2mm;
            }

            .header-divider {
              width: 100%;
              height: 1px;
              background-color: #000;
              margin-top: 2mm;
            }
            
            .sender-info {
              flex: 1;
              padding-right: 5mm;
            }
            
            .sender-name {
              font-weight: bold;
              font-size: 11pt;
              margin-bottom: 1mm;
              line-height: 1.1;
            }
            
            .sender-address {
              font-size: 8pt;
              margin-bottom: 0.8mm;
              line-height: 1.1;
            }
            
            .sender-details {
              font-size: 8pt;
              margin-bottom: 0.8mm;
              line-height: 1.1;
            }
            
            .document-info {
              text-align: right;
              flex-shrink: 0;
              min-width: 35mm;
              margin-left: auto;
            }
            
            .document-title {
              font-weight: bold;
              font-size: 11pt;
              margin-bottom: 1mm;
              line-height: 1.1;
            }
            
            .document-meta {
              font-size: 8pt;
              margin-bottom: 0.8mm;
              line-height: 1.1;
            }
            
            /* Customer/Order Section - Exact Match */
            .gp-section {
              height: 15mm;
              padding: 1mm 0;
              flex-shrink: 0;
              margin-bottom: 2mm;
            }
            .customer-line { font-size: 9pt; line-height: 1.2; margin-bottom: 2mm; font-weight: normal; }
            .order-details-section { width: 100%; }
            .order-line { display: flex; justify-content: space-between; align-items: center; font-size: 9pt; line-height: 1.2; margin-bottom: 0.5mm; width: 100%; }
            .po-detail { flex-shrink: 0; font-weight: normal; }
            .pattern-detail { flex-shrink: 0; text-align: right; font-weight: normal; }
            .qty-line { font-size: 8pt; line-height: 1.2; margin-bottom: 0; font-style: normal; font-weight: normal; font-family: inherit; }
            
            .recipient-info {
              flex: 1;
              font-size: 6pt;
              line-height: 1.2;
            }
            
            .order-details {
              text-align: right;
              font-size: 6pt;
              line-height: 1.2;
            }
            
            /* GP Content - Table area */
            .gp-content {
              flex: 1;
              margin: 1mm 0;
            }
            
            .table-content {
              height: 100%;
            }
            
            .gp-table {
              width: 100%;
              height: 100%;
              border-collapse: collapse;
              table-layout: fixed;
              font-size: 8pt;
            }
            
            .gp-table th {
              border: 1px solid #000;
              padding: 0.8mm 0.3mm;
              text-align: center;
              vertical-align: middle;
              font-weight: bold;
              font-size: 8pt;
              line-height: 1.0;
              height: 5mm;
            }
            
            .gp-table td {
              border: 1px solid #000;
              padding: 0.8mm 0.3mm;
              text-align: center;
              vertical-align: middle;
              font-size: 8pt;
              line-height: 1.0;
              height: 6.5mm;
            }
            
            .gp-table td.serial {
              text-align: center;
            }
            
            .gp-table td.mtr {
              text-align: center;
              padding-right: 1mm;
            }
            
            .gp-table td.total-label {
              text-align: center;
              font-weight: bold;
              padding-left: 1mm;
            }
            
            .gp-table td.total-value {
              text-align: center;
              font-weight: bold;
              padding-right: 1mm;
            }
            
            .gp-table th[style*="visibility: hidden"],
            .gp-table td[style*="visibility: hidden"] {
              border: none !important;
              background: transparent !important;
              color: transparent !important;
              visibility: hidden !important;
            }
            
            /* GP Footer - Matches template */
            .gp-footer {
              height: 12mm;
              display: flex;
              justify-content: space-between;
              align-items: flex-end;
              padding: 1.5mm 0 0 0;
              border-top: 1px solid #000;
              flex-shrink: 0;
              margin-top: auto;
            }
            
            .summary-info {
              display: flex;
              gap: 4mm;
              font-size: 8pt;
              line-height: 1.1;
              flex: 1;
            }
            
            .summary-item {
              white-space: nowrap;
            }
            
            .signature-area {
              text-align: right;
              font-size: 8pt;
              line-height: 1.1;
              flex-shrink: 0;
            }
            
            .signature-text { padding: 0; margin: 0; }
            
            /* Placeholder for empty GP */
            .gp-slip-placeholder {
              width: 100%;
              height: 133.5mm;
              border: 1px dashed #ccc;
              background: #f9f9f9;
              display: flex;
              align-items: center;
              justify-content: center;
              box-sizing: border-box;
            }
            
            .placeholder-text {
              font-size: 8pt;
              color: #666;
              text-align: center;
            }
            
            /* Print Media Queries */
            @media print {
              * {
                -webkit-print-color-adjust: exact !important;
                color-adjust: exact !important;
                box-sizing: border-box !important;
              }
              
              html, body {
                width: 210mm !important;
                height: 297mm !important;
                margin: 0 !important;
                padding: 0 !important;
                font-family: Arial, Helvetica, sans-serif !important;
                font-size: 8pt !important;
                line-height: 1.1 !important;
                background: white !important;
                overflow: visible !important;
                transform: none !important;
                zoom: 1 !important;
              }
              
              .dual-gp-container {
                width: 186mm !important;
                height: 277mm !important;
                margin: 10mm 12mm 0 12mm !important; /* top 10mm, bottom 0mm */
                page-break-after: always !important;
                page-break-inside: avoid !important;
                display: flex !important;
                flex-direction: column !important;
                gap: 10mm !important;
              }
              /* Double the space after even-numbered pages (2x vs odd-to-even) */
              .dual-gp-container:nth-of-type(even) { /* adds extra 10mm below even pages */
                margin-bottom: 10mm !important;
              }
              .dual-gp-container:nth-of-type(odd) {
                margin-bottom: 0 !important;
              }
              
              .dual-gp-container:last-child {
                page-break-after: avoid !important;
              }
              
              .gp-slip {
                width: 100% !important;
                height: 133.5mm !important;
                border: 1.5px solid #000 !important;
                padding: 4mm !important;
                margin: 0 !important;
                display: flex !important;
                flex-direction: column !important;
                page-break-inside: avoid !important;
                background: white !important;
              }
              
              .header-content {
                display: flex !important;
                justify-content: space-between !important;
                align-items: flex-start !important;
                margin-bottom: 2mm !important;
              }

              .header-divider {
                width: 100% !important;
                height: 1px !important;
                background-color: #000 !important;
                margin-top: 2mm !important;
                display: block !important;
              }

              .sender-info {
                display: table-cell !important;
                vertical-align: top !important;
                width: 65% !important;
                padding-right: 5mm !important;
              }

              .document-info {
                display: table-cell !important;
                vertical-align: top !important;
                width: auto !important;
                text-align: right !important;
                margin-left: auto !important;
              }

              .order-line {
                display: flex !important;
                justify-content: space-between !important;
                align-items: center !important;
                width: 100% !important;
                font-size: 9pt !important;
                margin-bottom: 0.5mm !important;
              }

              .po-detail { flex-shrink: 0 !important; }
              .pattern-detail { flex-shrink: 0 !important; text-align: right !important; }
              
              .gp-section {
                height: 12mm !important;
                padding: 1mm 0 !important;
                flex-shrink: 0 !important;
                page-break-inside: avoid !important;
              }
              
              .gp-content {
                flex: 1 !important;
                margin: 1mm 0 !important;
              }
              
              .gp-table {
                width: 100% !important;
                height: 100% !important;
                border-collapse: collapse !important;
                table-layout: fixed !important;
                font-size: 5.5pt !important;
              }
              
              .gp-table th,
              .gp-table td {
                border: 1px solid #000 !important;
                padding: 0.8mm 0.3mm !important;
                font-size: 8pt !important;
                line-height: 1.0 !important;
                overflow: hidden !important;
                white-space: nowrap !important;
                text-align: center !important;
              }
              
              .gp-footer {
                height: 10mm !important;
                margin-top: auto !important;
                flex-shrink: 0 !important;
                border-top: 1px solid #000 !important;
                page-break-inside: avoid !important;
                display: flex !important;
                justify-content: space-between !important;
                align-items: flex-end !important;
              }
              
              .summary-info {
                display: flex !important;
                gap: 4mm !important;
                font-size: 5pt !important;
                flex: 1 !important;
              }
              
              .signature-area {
                text-align: right !important;
                font-size: 4.5pt !important;
                flex-shrink: 0 !important;
              }
              
              .gp-slip-placeholder {
                display: none !important;
              }
            }
          </style>
          <script>
            window.onload = function() { 
              document.body.style.zoom = '1';
              document.body.style.transform = 'scale(1)';
              
              document.querySelectorAll('.dual-gp-container').forEach((container, index) => {
                const isLast = index === container.parentElement.children.length - 1;
                const isEven = (index + 1) % 2 === 0;
                container.style.pageBreakAfter = isLast ? 'avoid' : 'always';
                container.style.pageBreakInside = 'avoid';
                container.style.display = 'flex';
                container.style.flexDirection = 'column';
                container.style.marginBottom = isLast ? '0' : (isEven ? '20mm' : '10mm');
                container.offsetHeight;
              });
              
              document.querySelectorAll('.gp-slip').forEach(slip => {
                slip.style.pageBreakInside = 'avoid';
                slip.offsetHeight;
              });
              
              document.querySelectorAll('.gp-table').forEach(table => {
                table.style.tableLayout = 'fixed';
                table.style.width = '100%';
                table.offsetHeight;
              });
              
              document.querySelectorAll('.gp-footer').forEach(footer => {
                footer.style.marginTop = 'auto';
                footer.style.flexShrink = '0';
                footer.offsetHeight;
              });
              
              setTimeout(() => {
                console.log('A4 dual GP layout ready for printing');
                console.log('Total dual containers:', document.querySelectorAll('.dual-gp-container').length);
                console.log('Total GP slips:', document.querySelectorAll('.gp-slip').length);
                window.print();
              }, 4000);
            };
          </script>
        </head>
        <body>
          ${allPagesHTML}
        </body>
        </html>
      `;

      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const popup = window.open(url, 'a4DualGpWindow', 'width=900,height=700,scrollbars=yes,resizable=yes');
      
      if (!popup) {
        const link = document.createElement('a');
        link.href = url;
        link.download = `a4-dual-gate-pass-${printJob.client.name}-${new Date().toISOString().split('T')[0]}.html`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        alert('A4 Dual Gate Pass file downloaded. Open it and print.');
      } else {
        setTimeout(() => URL.revokeObjectURL(url), 10000);
      }
    } catch (error) {
      console.error('A4 dual gate pass generation error:', error);
      alert('Failed to generate A4 dual gate pass. Please try again.');
    }
  };

  const USE_A4_DUAL_GP = process.env.REACT_APP_USE_A4_DUAL_GP === 'true' || false;
  const generateGatePass = async (printJob) => {
    if (USE_A4_DUAL_GP) {
      return generateA4DualGatePass(printJob);
    }
    return generateLegacyA5GatePass(printJob);
  };

  // Utility function to safely format numbers
  const formatNumber = (value) => {
    if (value === null || value === undefined || value === '') return 'N/A';
    try {
      const num = parseFloat(value);
      return isNaN(num) ? 'N/A' : num.toFixed(2);
    } catch (error) {
      console.error('Error formatting number:', error);
      return 'N/A';
    }
  };

  // State for master data
  const [clients, setClients] = useState([]);
  const [items, setItems] = useState([]);

  // State for system settings
  const [globalSettings, setGlobalSettings] = useState({
    startingRollNumber: 1001,
    currentRollNumber: 1001
  });

  // State for print jobs and tracking
  const [printJobs, setPrintJobs] = useState([]);

  // UI state
  const [activeTab, setActiveTab] = useState('print');
  const [showAddClient, setShowAddClient] = useState(false);
  const [showAddItem, setShowAddItem] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [historyTitle, setHistoryTitle] = useState('');
  const [historyType, setHistoryType] = useState('');
  
  // Reports state
  const [reportType, setReportType] = useState(''); // 'item', 'user', or 'all'
  const [selectedReportItem, setSelectedReportItem] = useState(null);
  const [reportHistoryData, setReportHistoryData] = useState([]);
  
  // History state
  const [historyLog, setHistoryLog] = useState([]);
  const [selectedJobs, setSelectedJobs] = useState(new Set());
  const [selectAllJobs, setSelectAllJobs] = useState(false);
  const [selectedMTRRows, setSelectedMTRRows] = useState(new Set());
  const [selectAllMTRRows, setSelectAllMTRRows] = useState(false);
  const [rollSearchQuery, setRollSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  // Generate history log (defined once; no duplicate definitions)
  const generateHistoryLog = useCallback(() => {
    const dateGroups = new Map();
    printJobs.forEach(job => {
      const date = formatDate(job.timestamp);
      const clientName = job.client.name;
      const itemName = `${job.item.patternNumber} - ${job.item.description}`;
      const totalRolls = job.labels.length;
      const totalMtr = job.labels.reduce((sum, label) => sum + (parseFloat(label.quantity) || 0), 0);
      if (!dateGroups.has(date)) {
        dateGroups.set(date, []);
      }
      dateGroups.get(date).push({ id: job.id, job, client: clientName, item: itemName, totalRolls, totalMtr });
    });
    const logData = Array.from(dateGroups.entries())
      .map(([date, entries]) => ({ date, entries }))
      .sort((a, b) => new Date(b.date.split('/').reverse().join('-')) - new Date(a.date.split('/').reverse().join('-')));
    setHistoryLog(logData);
  }, [printJobs]);

  // Print job state
  const [currentPrintJob, setCurrentPrintJob] = useState({
    clientId: '',
    itemId: '',
    numberOfRolls: 1,
    mtrValues: []
  });



  // Load data from API on component mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [clientsRes, itemsRes, settingsRes, printJobsRes] = await Promise.all([
        axios.get('/api/clients'),
        axios.get('/api/items'),
        axios.get('/api/settings'),
        axios.get('/api/print-jobs')
      ]);

      setClients(clientsRes.data);
      setItems(itemsRes.data);
      
      // Transform print jobs data to match the expected format
      // Group by print job ID since the server returns one row per label
      const jobMap = new Map();
      
      printJobsRes.data.forEach(row => {
        if (!jobMap.has(row.id)) {
          // Create new print job entry
          jobMap.set(row.id, {
            id: row.id,
            timestamp: row.created_at,
            client: {
              name: row.client_name,
              location: row.client_location,
              code: row.client_code
            },
            item: {
              patternNumber: row.pattern_number,
              description: row.item_description,
              quality: row.quality,
              net_weight: row.net_weight,
              gross_weight: row.gross_weight
            },
            totalLabels: row.total_labels,
            labels: []
          });
        }
        
        // Add label data if roll_number exists
        if (row.roll_number) {
          jobMap.get(row.id).labels.push({
            roll_number: row.roll_number,
            quantity: row.mtr_value
          });
        }
      });
      
      // Convert map to array and sort by timestamp
      const transformedPrintJobs = Array.from(jobMap.values())
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      
      setPrintJobs(transformedPrintJobs);
      
      // Update settings
      const settings = settingsRes.data;
      setGlobalSettings({
        startingRollNumber: parseInt(settings.starting_roll_number) || 1001,
        currentRollNumber: parseInt(settings.current_roll_number) || 1001
      });
    } catch (error) {
      console.error('Error loading data:', error);
      alert('Failed to load data from database');
    } finally {
      setLoading(false);
    }
  };

  // Generate history log whenever printJobs changes
  useEffect(() => {
    generateHistoryLog();
  }, [generateHistoryLog]);

  // Search function for roll numbers
  const searchRollNumber = () => {
    if (!rollSearchQuery.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    
    // Simulate a small delay for better UX
    setTimeout(() => {
      const query = rollSearchQuery.trim().toLowerCase();
      const results = [];

      printJobs.forEach(job => {
        job.labels.forEach(label => {
          if (label.roll_number && label.roll_number.toString().includes(query)) {
            results.push({
              job: job,
              label: label,
              rollNumber: label.roll_number,
              mtr: label.quantity,
              clientName: job.client.name,
              itemName: `${job.item.patternNumber} - ${job.item.description}`,
              date: formatDate(job.timestamp)
            });
          }
        });
      });

      setSearchResults(results);
      setShowSearchResults(true);
      setIsSearching(false);
    }, 300);
  };

  // Clear search results
  const clearSearch = () => {
    setRollSearchQuery('');
    setSearchResults([]);
    setShowSearchResults(false);
    setIsSearching(false);
  };

  // Client management functions
  const addClient = async (client) => {
    try {
      const response = await axios.post('/api/clients', client);
      setClients([...clients, response.data]);
      setShowAddClient(false);
    } catch (error) {
      console.error('Error adding client:', error);
      alert('Failed to add client');
    }
  };

  const updateClient = async (id, updatedClient) => {
    try {
      const response = await axios.put(`/api/clients/${id}`, updatedClient);
      setClients(clients.map(c => c.id === id ? response.data : c));
      setEditingClient(null);
    } catch (error) {
      console.error('Error updating client:', error);
      alert('Failed to update client');
    }
  };

  const deleteClient = async (id) => {
    try {
      await axios.delete(`/api/clients/${id}`);
      setClients(clients.filter(c => c.id !== id));
    } catch (error) {
      console.error('Error deleting client:', error);
      alert('Failed to delete client');
    }
  };

  // Item management functions
  const addItem = async (item) => {
    try {
      const response = await axios.post('/api/items', item);
      setItems([...items, response.data]);
      setShowAddItem(false);
    } catch (error) {
      console.error('Error adding item:', error);
      alert('Failed to add item');
    }
  };

  const updateItem = async (id, updatedItem) => {
    try {
      const response = await axios.put(`/api/items/${id}`, updatedItem);
      setItems(items.map(i => i.id === id ? response.data : i));
      setEditingItem(null);
    } catch (error) {
      console.error('Error updating item:', error);
      alert('Failed to update item');
    }
  };

  const deleteItem = async (id) => {
    try {
      await axios.delete(`/api/items/${id}`);
      setItems(items.filter(i => i.id !== id));
    } catch (error) {
      console.error('Error deleting item:', error);
      alert('Failed to delete item');
    }
  };

  // Print job functions
  const handlePrintJobChange = (field, value) => {
    if (field === 'numberOfRolls') {
      const newCount = parseInt(value) || 0;
      const existingValues = currentPrintJob.mtrValues || [];
      let adjustedValues = existingValues.slice(0, newCount);
      if (newCount > existingValues.length) {
        adjustedValues = [
          ...existingValues,
          ...Array(newCount - existingValues.length).fill('')
        ];
      }
      setCurrentPrintJob({
        ...currentPrintJob,
        [field]: newCount,
        mtrValues: adjustedValues
      });
    } else {
      setCurrentPrintJob({
        ...currentPrintJob,
        [field]: value
      });
    }
  };

  const handleMTRChange = (index, value) => {
    // Only allow numbers with up to 2 decimal points
    const regex = /^\d*\.?\d{0,2}$/;
    if (value === '' || regex.test(value)) {
      const newMTRValues = [...currentPrintJob.mtrValues];
      newMTRValues[index] = value;
      setCurrentPrintJob({
        ...currentPrintJob,
        mtrValues: newMTRValues
      });
    }
  };

  // Remove last roll function
  const removeLastRoll = () => {
    const newCount = Math.max(0, (parseInt(currentPrintJob.numberOfRolls) || 0) - 1);
    const newMTRValues = [...currentPrintJob.mtrValues];
    newMTRValues.pop(); // Remove the last element
    
    setCurrentPrintJob({
      ...currentPrintJob,
      numberOfRolls: newCount,
      mtrValues: newMTRValues
    });
  };

  // Delete a specific row
  const deleteRow = (index) => {
    const newCount = Math.max(0, (parseInt(currentPrintJob.numberOfRolls) || 0) - 1);
    const newMTRValues = [...currentPrintJob.mtrValues];
    newMTRValues.splice(index, 1); // Remove the element at the specified index
    
    setCurrentPrintJob({
      ...currentPrintJob,
      numberOfRolls: newCount,
      mtrValues: newMTRValues
    });
  };

  // Insert a new row at a specific position
  const insertRow = (index) => {
    const newCount = (parseInt(currentPrintJob.numberOfRolls) || 0) + 1;
    const newMTRValues = [...currentPrintJob.mtrValues];
    newMTRValues.splice(index, 0, ''); // Insert empty string at the specified index
    
    setCurrentPrintJob({
      ...currentPrintJob,
      numberOfRolls: newCount,
      mtrValues: newMTRValues
    });
  };

  // Handle job selection for collated printing
  const handleJobSelection = (jobId) => {
    const newSelectedJobs = new Set(selectedJobs);
    if (newSelectedJobs.has(jobId)) {
      newSelectedJobs.delete(jobId);
    } else {
      newSelectedJobs.add(jobId);
    }
    setSelectedJobs(newSelectedJobs);
  };

  // Handle select all jobs
  const handleSelectAllJobs = () => {
    if (selectAllJobs) {
      setSelectedJobs(new Set());
      setSelectAllJobs(false);
    } else {
      const allJobIds = new Set();
      historyLog.forEach(dateGroup => {
        dateGroup.entries.forEach(entry => {
          allJobIds.add(entry.job.id);
        });
      });
      setSelectedJobs(allJobIds);
      setSelectAllJobs(true);
    }
  };

  // Handle MTR row selection for multi-select
  const handleMTRRowSelection = (index) => {
    const newSelectedMTRRows = new Set(selectedMTRRows);
    if (newSelectedMTRRows.has(index)) {
      newSelectedMTRRows.delete(index);
    } else {
      newSelectedMTRRows.add(index);
    }
    setSelectedMTRRows(newSelectedMTRRows);
  };

  // Handle select all MTR rows
  const handleSelectAllMTRRows = () => {
    if (selectAllMTRRows) {
      setSelectedMTRRows(new Set());
      setSelectAllMTRRows(false);
    } else {
      const allMTRRowIndices = new Set();
      const numRolls = parseInt(currentPrintJob.numberOfRolls) || 0;
      for (let i = 0; i < numRolls; i++) {
        allMTRRowIndices.add(i);
      }
      setSelectedMTRRows(allMTRRowIndices);
      setSelectAllMTRRows(true);
    }
  };

  // Delete multiple selected MTR rows
  const deleteSelectedMTRRows = () => {
    if (selectedMTRRows.size === 0) {
      alert('Please select at least one row to delete.');
      return;
    }

    const numRolls = parseInt(currentPrintJob.numberOfRolls) || 0;
    const newCount = Math.max(0, numRolls - selectedMTRRows.size);
    const newMTRValues = [...currentPrintJob.mtrValues];
    
    // Remove selected rows in reverse order to maintain correct indices
    const sortedIndices = Array.from(selectedMTRRows).sort((a, b) => b - a);
    sortedIndices.forEach(index => {
      newMTRValues.splice(index, 1);
    });
    
    setCurrentPrintJob({
      ...currentPrintJob,
      numberOfRolls: newCount,
      mtrValues: newMTRValues
    });
    
    // Clear selection
    setSelectedMTRRows(new Set());
    setSelectAllMTRRows(false);
  };

  // Generate collated GP for multiple selected jobs
  const generateCollatedGatePass = async () => {
    if (selectedJobs.size === 0) {
      alert('Please select at least one job to print GP.');
      return;
    }

    try {
      const gpNo = getAndIncrementGpNumber();
      const poNo = (typeof window !== 'undefined' ? (window.prompt('Enter P.O.No.', '') || '') : '');
      
      // Get all selected jobs
      const selectedJobData = [];
      historyLog.forEach(dateGroup => {
        dateGroup.entries.forEach(entry => {
          if (selectedJobs.has(entry.job.id)) {
            selectedJobData.push(entry.job);
          }
        });
      });

      // Generate HTML for each job separately (collated without merging)
      let allPagesHTML = '';
      
      selectedJobData.forEach((printJob, jobIndex) => {
        const labels = (printJob.labels || []).map(l => ({
          roll: l.roll_number || '',
          qty: parseFloat(l.quantity) || 0
        }));
        
        // Layout constants matching sample: 7 column-pairs across, 10 rows per pair
        const rowsPerGroup = 10;
        const groupsPerPage = 7; // each group is two columns: S.No and MTR
        const labelsPerPage = rowsPerGroup * groupsPerPage; // 70
        const totalPages = Math.max(1, Math.ceil(labels.length / labelsPerPage));
        console.log(`Generating ${totalPages} pages for ${labels.length} labels`);

        const buildRollNumberSummary = (pageIndex = null) => {
          if (labels.length === 0) return '';
          
          let labelsToProcess = labels;
          if (pageIndex !== null) {
            // For specific page, only process labels for that page
            const start = pageIndex * labelsPerPage;
            labelsToProcess = labels.slice(start, start + labelsPerPage);
          }
          
          const sorted = [...labelsToProcess].map(l => Number(l.roll)).filter(n => !isNaN(n)).sort((a,b) => a - b);
          if (sorted.length === 0) return '';
          const ranges = [];
          let start = sorted[0];
          let prev = sorted[0];
          for (let i = 1; i < sorted.length; i++) {
            const current = sorted[i];
            if (current === prev + 1) {
              prev = current;
              continue;
            }
            ranges.push(start === prev ? String(start) : `${start}-${prev}`);
            start = current;
            prev = current;
          }
          ranges.push(start === prev ? String(start) : `${start}-${prev}`);
          return ranges.join(', ');
        };

        const buildTableForPage = (pageIndex) => {
          const start = pageIndex * labelsPerPage;
          const pageLabels = labels.slice(start, start + labelsPerPage);
          
          // Create 7 groups, each with 10 rows
          const groups = Array.from({ length: groupsPerPage }).map((_, groupIndex) => {
            const gStart = groupIndex * rowsPerGroup;
            const gItems = pageLabels.slice(gStart, gStart + rowsPerGroup);
            const groupTotal = gItems.reduce((sum, it) => sum + (it?.qty || 0), 0);
            return { items: gItems, total: groupTotal };
          });

          // Generate header row with 7 pairs of S.No and MTR columns
          const headerCells = Array.from({ length: groupsPerPage }).map((_, groupIndex) => {
            const hasDataInGroup = groups[groupIndex].items.some(item => item);
            if (hasDataInGroup) {
              return `
                <th>S.No</th>
                <th>MTR</th>
              `;
            }
            return `
              <th style="visibility: hidden;">S.No</th>
              <th style="visibility: hidden;">MTR</th>
            `;
          }).join('');

          // Generate rows only for filled data, but maintain consistent cell sizes
          const filledRows = [];
          for (let rowIndex = 0; rowIndex < rowsPerGroup; rowIndex++) {
            const rowCells = groups.map((group, groupIndex) => {
              const globalIndex = pageIndex * labelsPerPage + groupIndex * rowsPerGroup + rowIndex;
              const serialNumber = globalIndex + 1; // 1-based running S.No
              const item = group.items[rowIndex];
              if (item) {
                return `
                  <td class="serial">${serialNumber}</td>
                  <td class="mtr">${item.qty.toFixed(2)}</td>
                `;
              }
              return `
                <td class="serial" style="visibility: hidden;">${serialNumber}</td>
                <td class="mtr" style="visibility: hidden;"></td>
              `;
            }).join('');
            
            // Only add row if at least one cell has data
            const hasData = groups.some(group => group.items[rowIndex]);
            if (hasData) {
              filledRows.push(`<tr>${rowCells}</tr>`);
            }
          }
          
          const bodyRows = filledRows.join('');

          // Generate totals row
          const totalsRow = `<tr>
            ${groups.map(g => {
              const hasData = g.items.some(item => item);
              if (hasData) {
                return `
                  <td class="total-label">Total</td>
                  <td class="total-value">${g.total > 0 ? g.total.toFixed(2) : ''}</td>
                `;
              }
              return `
                <td class="total-label" style="visibility: hidden;">Total</td>
                <td class="total-value" style="visibility: hidden;"></td>
              `;
            }).join('')}
          </tr>`;

          return `
            <table class="gp-table">
              <thead>
                <tr>${headerCells}</tr>
              </thead>
              <tbody>
                ${bodyRows}
                ${totalsRow}
              </tbody>
            </table>
          `;
        };

        const buildPageWithHeaderFooter = (pageIndex) => {
          const tableContent = buildTableForPage(pageIndex);
          const totalMtr = labels.reduce((sum, l) => sum + l.qty, 0);
          const totalRolls = labels.length;
          
          return `
            <div class="page-container">
              <div class="header">
                <div class="header-content">
                  <div class="sender-info">
                    <div class="sender-name">SNSM TEXTILES</div>
                    <div class="sender-address">3/297D, Ramaiyan Kadu, P.M. Nagar, Seelanaickenpatti,</div>
                    <div class="sender-address">Salem - 636201, Tamil Nadu.</div>
                    <div class="sender-details">GSTIN: 33AHXPS4027M1ZN</div>
                    <div class="sender-details">PH: +91-9940797757</div>
                  </div>
                  <div class="document-info">
                    <div class="document-title">Goods Packing Slip</div>
                    <div class="document-meta">No: <strong>${gpNo}</strong></div>
                    <div class="document-meta">Date: <strong>${new Date().toLocaleDateString('en-GB')}</strong></div>
                  </div>
                </div>
              </div>

              <div class="section">
                <div class="recipient-info">To, M/s. ${printJob.client.name}, ${printJob.client.location || ''}</div>
                <div class="order-details">
                  <div>PO No: <strong>${poNo || '-'}</strong></div>
                  <div>PTTN# <strong>${printJob.item.patternNumber}</strong> – ${printJob.item.description}</div>
                </div>
              </div>

              <div class="content">
                <div class="table-content">
                  ${tableContent}
                </div>

                <div class="footer">
                  <div class="summary-info">
                    <div class="summary-item">Total PCS: <strong>${totalRolls}</strong></div>
                    <div class="summary-item">Total MTR: <strong>${totalMtr.toFixed(2)}</strong></div>
                    <div class="summary-item">Roll Nos: <strong>${buildRollNumberSummary(pageIndex)}</strong></div>
                  </div>
                  <div class="signature-area">
                    <div>For SNSM TEXTILES,</div>
                    <div class="signature-text"> </div>
                  </div>
                </div>
              </div>
            </div>
          `;
        };

        // Generate all pages for this job
        const jobPagesHTML = Array.from({ length: totalPages }).map((_, pageIndex) => 
          buildPageWithHeaderFooter(pageIndex)
        ).join('');
        
        allPagesHTML += jobPagesHTML;
      });

      const html = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8" />
          <title>Collated Goods Packing Slips - ${selectedJobData.length} Jobs</title>
          <style>
            @page { size: A5 landscape; margin: 10mm 12mm; }
            body { 
              margin: 0; 
              font-family: Arial, Helvetica, sans-serif; 
              color: #000; 
              font-size: 7pt; 
              line-height: 1.4;
            }
            
            div { display: block; }
            
            .page-container { 
              border: 1px solid #000; 
              margin: 4mm; 
              padding: 6mm; 
              width: 210mm;
              height: 148mm;
              box-sizing: border-box; 
              display: flex;
              flex-direction: column;
              page-break-after: always;
              page-break-inside: avoid;
            }
            
            .header { padding: 3mm 0; border-bottom: 1px solid #000; flex-shrink: 0; }
            .header-content { display: flex; justify-content: space-between; align-items: flex-start; }
            
            .sender-info { flex: 1; }
            .sender-name { 
              font-weight: bold; 
              font-size: 8pt; 
              margin-bottom: 2mm; 
              display: block;
              line-height: 1.4;
            }
            .sender-address { 
              margin-bottom: 1.5mm; 
              font-size: 6pt; 
              display: block;
              line-height: 1.4;
            }
            .sender-details { 
              margin-bottom: 1.5mm; 
              font-size: 6pt; 
              display: block;
              line-height: 1.4;
            }
            
            .document-info { text-align: right; }
            .document-title { 
              font-weight: bold; 
              font-size: 9pt; 
              margin-bottom: 1.5mm; 
              display: block;
              line-height: 1.4;
            }
            .document-meta { 
              margin-bottom: 1mm; 
              font-size: 6pt; 
              display: block;
              line-height: 1.4;
            }
            
            .sender-name, .sender-address, .sender-details, 
            .document-title, .document-meta,
            .recipient-info, .order-details div {
              display: block !important;
              white-space: normal !important;
            }
            
            .section { display: flex; justify-content: space-between; align-items: flex-start; padding: 2mm 0; flex-shrink: 0; }
            .recipient-info { flex: 1; font-size: 6pt; display: block; line-height: 1.4; }
            .order-details { text-align: right; font-size: 6pt; }
            
            .content { margin: 2mm 0; flex: 1; display: flex; flex-direction: column; }
            .table-content { flex: 1; }
            .gp-table { width: 100%; border-collapse: collapse; table-layout: fixed; font-size: 6pt; margin-top: 2mm; }
            .gp-table th, .gp-table td { 
              border: 1px solid #000; 
              padding: 1.5mm 1mm; 
              text-align: center; 
              vertical-align: middle; 
              min-height: 5mm;
              white-space: nowrap;
              line-height: 1.2;
            }
            .gp-table th { font-weight: bold; font-size: 6pt; }
            .gp-table td.serial { text-align: center; }
            .gp-table td.mtr { text-align: right; }
            .gp-table td.total-label { text-align: left; font-weight: bold; }
            .gp-table td.total-value { text-align: right; font-weight: bold; }
            .gp-table th[style*="visibility: hidden"], .gp-table td[style*="visibility: hidden"] { border: none; background: transparent; }
            
            .footer { display: flex; justify-content: space-between; align-items: flex-end; padding: 2mm 0; border-top: 1px solid #000; flex-shrink: 0; margin-top: auto; }
            .summary-info { 
              display: flex; 
              gap: 6mm; 
              font-size: 6pt; 
              line-height: 1.3;
            }
            .summary-item { 
              margin-bottom: 1mm; 
              white-space: nowrap;
            }
            .signature-area { text-align: right; font-size: 5pt; }
            .signature-text { padding: 0; margin: 0; }
            
            .page-container:last-child { page-break-after: avoid; }
            
            @media print { 
              * { -webkit-print-color-adjust: exact !important; color-adjust: exact !important; }
              
              body { 
                margin: 0 !important; 
                line-height: 1.4 !important;
                font-family: Arial, Helvetica, sans-serif !important;
                -webkit-font-smoothing: antialiased;
              }
              
              div, span, p { display: block !important; }
              
              .page-container { 
                page-break-after: always !important; 
                page-break-inside: avoid !important;
                border: 1px solid #000 !important;
                margin: 0 !important;
                padding: 6mm !important;
                width: 210mm !important;
                height: 148mm !important;
                box-sizing: border-box !important;
                display: block !important;
                position: relative !important;
              }
              
              .page-container:last-child { page-break-after: avoid !important; }
              
              .header { 
                padding: 3mm 0 !important; 
                border-bottom: 1px solid #000 !important; 
                page-break-inside: avoid !important;
                display: block !important;
              }
              
              .header-content { 
                display: flex !important; 
                justify-content: space-between !important; 
                align-items: flex-start !important; 
              }
              
              .sender-name { 
                font-weight: bold !important; 
                font-size: 9pt !important; 
                margin-bottom: 2mm !important; 
                display: block !important;
                line-height: 1.4 !important;
              }
              
              .sender-address, .sender-details { 
                margin-bottom: 1.5mm !important; 
                font-size: 7pt !important; 
                display: block !important;
                line-height: 1.4 !important;
              }
              
              .document-title { 
                font-weight: bold !important; 
                font-size: 10pt !important; 
                margin-bottom: 1.5mm !important; 
                display: block !important;
                line-height: 1.4 !important;
              }
              
              .document-meta { 
                margin-bottom: 1mm !important; 
                font-size: 7pt !important; 
                display: block !important;
                line-height: 1.4 !important;
              }
              
              .sender-name, .sender-address, .sender-details, 
              .document-title, .document-meta,
              .recipient-info, .order-details div {
                display: block !important;
                white-space: normal !important;
                word-wrap: break-word !important;
              }
              
              .section { 
                display: flex !important; 
                justify-content: space-between !important; 
                padding: 2mm 0 !important; 
                page-break-inside: avoid !important;
              }
              
              .recipient-info, .order-details { font-size: 7pt !important; }
              
              .gp-table { 
                width: 100% !important; 
                border-collapse: collapse !important; 
                table-layout: fixed !important; 
                font-size: 7pt !important;
                margin-top: 2mm !important;
                page-break-inside: avoid !important;
              }
              
              .gp-table th, .gp-table td { 
                border: 1px solid #000 !important; 
                padding: 1.5mm 1mm !important; 
                text-align: center !important; 
                vertical-align: middle !important; 
                min-height: 5mm !important;
                white-space: nowrap !important;
                line-height: 1.2 !important;
                font-size: 7pt !important;
                overflow: hidden !important;
              }
              
              .footer { 
                display: flex !important; 
                justify-content: space-between !important; 
                padding: 2mm 0 !important; 
                border-top: 1px solid #000 !important; 
                margin-top: auto !important;
                page-break-inside: avoid !important;
              }
              
              .summary-info {
                display: flex !important;
                gap: 8mm !important;
                font-size: 8pt !important;
                line-height: 1.2 !important;
                flex: 1 !important;
              }
              
              .summary-item {
                white-space: nowrap !important;
                margin-bottom: 0 !important;
              }
              
              .signature-area {
                text-align: right !important;
                font-size: 7pt !important;
                line-height: 1.3 !important;
                flex-shrink: 0 !important;
              }
              
              .signature-text { padding: 0; margin: 0; }
              
              /* Hide empty cells properly */
              .gp-table th[style*="visibility: hidden"],
              .gp-table td[style*="visibility: hidden"] {
                border: none !important;
                background: transparent !important;
                color: transparent !important;
                visibility: hidden !important;
              }
              
              /* Force content visibility and prevent overflow */
              .page-container * {
                max-width: 100% !important;
                overflow: visible !important;
              }
              
              /* Ensure all text elements display properly on all pages */
              .sender-name, .sender-address, .sender-details, 
              .document-title, .document-meta,
              .recipient-info, .order-details div {
                display: block !important;
                white-space: normal !important;
                word-wrap: break-word !important;
              }
              
              /* Additional bottom section protection */
              .page-container::after {
                content: '' !important;
                display: block !important;
                height: 2mm !important;
                width: 100% !important;
                flex-shrink: 0 !important;
              }
            }
          </style>
          <script>
          window.onload = function() { 
            // Enhanced multi-page printing setup with bottom section protection
            document.body.style.zoom = '1';
            document.body.style.transform = 'scale(1)';
            
            // Force all page containers to be properly sized with footer protection
            document.querySelectorAll('.page-container').forEach((container, index) => {
              container.style.transform = 'scale(0.95)';
              container.style.transformOrigin = 'top left';
              container.style.pageBreakAfter = index === container.parentElement.children.length - 1 ? 'avoid' : 'always';
              container.style.pageBreakInside = 'avoid';
              container.style.minHeight = '130mm';
              container.style.maxHeight = '130mm';
              container.style.display = 'flex';
              container.style.flexDirection = 'column';
              container.offsetHeight;
            });
            
            // Force table layout recalculation for all pages
            document.querySelectorAll('.gp-table').forEach(table => {
              table.style.tableLayout = 'fixed';
              table.style.width = '100%';
              table.style.fontSize = '6pt';
              table.offsetHeight;
            });
            
            // Ensure footer positioning on all pages
            document.querySelectorAll('.footer').forEach(footer => {
              footer.style.marginTop = 'auto';
              footer.style.flexShrink = '0';
              footer.style.position = 'relative';
              footer.style.bottom = '0';
              footer.offsetHeight;
            });
            
            // Ensure all pages are rendered before printing
            setTimeout(() => {
              // Final layout check with footer verification
              document.body.offsetHeight;
              console.log('Total pages to print:', document.querySelectorAll('.page-container').length);
              console.log('Footer elements found:', document.querySelectorAll('.footer').length);
              window.print();
            }, 3500); // Increased to 3500ms for footer positioning
          };
          </script>
        </head>
        <body>
          <div class="gp-page">
            ${allPagesHTML}
          </div>
        </body>
        </html>
      `;

      // Generate HTML
      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const popup = window.open(url, 'gpWindow', 'width=900,height=700,scrollbars=yes,resizable=yes');
      if (!popup) {
        const link = document.createElement('a');
        link.href = url;
        link.download = `collated-gate-pass-${selectedJobData.length}-jobs-${new Date().toISOString().split('T')[0]}.html`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        alert('Collated Gate Pass file downloaded. Open it and print.');
      } else {
        setTimeout(() => URL.revokeObjectURL(url), 10000);
      }
    } catch (error) {
      console.error('Collated gate pass generation error:', error);
      alert('Failed to generate collated gate pass.');
    }
  };

  const getAndIncrementGpNumber = () => {
    try {
      const key = 'gpNumber';
      const current = parseInt(localStorage.getItem(key) || '1', 10);
      const next = isNaN(current) ? 1 : current + 1;
      localStorage.setItem(key, String(next));
      return isNaN(current) ? 1 : current;
    } catch (e) {
      return Math.floor(Date.now() / 1000); // fallback unique-ish
    }
  };

  const generatePrintJob = async () => {
    if (!currentPrintJob.clientId || !currentPrintJob.itemId || currentPrintJob.mtrValues.some(mtr => !mtr)) {
      alert('Please fill all required fields');
      return;
    }

    // Find client and item for validation (not used in current implementation)
    // const client = clients.find(c => c.id === parseInt(currentPrintJob.clientId));
    // const item = items.find(i => i.id === parseInt(currentPrintJob.itemId));
    
    const labels = currentPrintJob.mtrValues.map((mtr, index) => ({
      roll_number: globalSettings.currentRollNumber + index,
      quantity: parseFloat(mtr)
    }));

    try {
      const response = await axios.post('/api/print-jobs', {
        client_id: parseInt(currentPrintJob.clientId),
        item_id: parseInt(currentPrintJob.itemId),
        labels: labels
      });

      // Transform the response data to match our expected format
      const jobMap = new Map();
      
      response.data.forEach(row => {
        if (!jobMap.has(row.id)) {
          jobMap.set(row.id, {
            id: row.id,
            timestamp: row.created_at,
            client: {
              name: row.client_name,
              location: row.client_location,
              code: row.client_code
            },
            item: {
              patternNumber: row.pattern_number,
              description: row.item_description
            },
            totalLabels: row.total_labels,
            labels: []
          });
        }
        
        if (row.roll_number) {
          jobMap.get(row.id).labels.push({
            roll_number: row.roll_number,
            quantity: row.mtr_value
          });
        }
      });
      
      const newPrintJob = jobMap.get(response.data[0].id);
      setPrintJobs([newPrintJob, ...printJobs]);
      
      // Update global roll number
      setGlobalSettings({
        ...globalSettings,
        currentRollNumber: globalSettings.currentRollNumber + labels.length
      });
      
      // Generate PDF and print
      setTimeout(() => {
        generatePDF(newPrintJob);
      }, 100);

      // Reset form
      setCurrentPrintJob({
        clientId: '',
        itemId: '',
        numberOfRolls: 1,
        mtrValues: []
      });
    } catch (error) {
      console.error('Error creating print job:', error);
      alert('Failed to create print job');
    }
  };

  const generatePDF = (printJob) => {
    try {
      const labelsPerSheet = 12; // Changed back to 12 labels per sheet (6x2 grid)
      const totalSheets = Math.ceil(printJob.labels.length / labelsPerSheet);
      const clientDisplayName = printJob.client.code || printJob.client.name;
      
      let htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Labels - ${printJob.client.name}</title>
          <meta charset="UTF-8">
          <style>
            @page { margin: 11.5mm 5.5mm; }
            body { margin: 0; padding: 0; font-family: Arial, Helvetica, sans-serif; font-size: 12pt; }
            /* Sheet fits page size; page margins handled by @page */
            .sheet { width: 210mm; height: 297mm; margin: 0 auto; position: relative; page-break-after: always; padding: 0; box-sizing: border-box; }
            .sheet:last-child { page-break-after: avoid; }
            /* Labels grid: 6 rows, 2 columns (12 labels total) */
            .label-grid { display: grid; grid-template-columns: repeat(2, 1fr); grid-template-rows: repeat(6, 1fr); height: 100%; padding: 11.5mm 5.5mm; box-sizing: border-box; gap: 5.5mm; }
            /* Label styling to match the image exactly */
            .label { 
              padding: 3.5mm; 
              display: flex; 
              flex-direction: column; 
              justify-content: flex-start; 
              align-items: flex-start; 
              text-align: left; 
              line-height: 1.2; 
              font-size: 11pt; 
              background: white; 
              border: 1px solid #fff; 
              border-radius: 8px; 
              box-sizing: border-box; 
              font-family: Arial, Helvetica, sans-serif;
            }
            .supplier { 
              font-weight: 700; 
              margin-bottom: 4px; 
              font-size: 12pt; 
              font-family: Arial, Helvetica, sans-serif;
              text-align: center;
              width: 100%;
            }
            .client { 
              margin-bottom: 4px; 
              font-size: 12pt; 
              font-family: Arial, Helvetica, sans-serif;
              text-align: left;
            }
            .pattern { 
              margin-bottom: 6px; 
              font-size: 11pt; 
              font-family: Arial, Helvetica, sans-serif;
              text-align: left;
            }
            .roll-info { 
              width: 100%; 
              display: flex; 
              flex-direction: column; 
              justify-content: flex-start; 
              align-items: flex-start; 
              gap: 3px; 
              font-size: 12pt; 
              margin-top: 4px; 
              font-family: Arial, Helvetica, sans-serif;
            }
            .roll-no, .qnty { 
              font-size: 12pt; 
              font-family: Arial, Helvetica, sans-serif;
              text-align: left;
            }
            .print-button { position: fixed; top: 20px; right: 20px; z-index: 1000; padding: 10px 20px; background: #667eea; color: white; border: none; border-radius: 5px; cursor: pointer; font-size: 14px; }
            .print-button:hover { background: #5a67d8; }
            @media print { 
              body { margin: 0; padding: 0; } 
              .sheet { margin: 0; page-break-after: always; }
              .sheet:last-child { page-break-after: avoid; }
              .print-button { display: none; }
            }
          </style>
          <script>
            function printLabels() {
              window.print();
            }
            // Auto-print when page loads
            window.onload = function() {
              setTimeout(() => {
                window.print();
              }, 500);
            };
          </script>
        </head>
        <body>
          <button class="print-button" onclick="printLabels()">Print Labels</button>
      `;

      for (let sheet = 0; sheet < totalSheets; sheet++) {
        htmlContent += '<div class="sheet"><div class="label-grid">';
        
        for (let i = 0; i < labelsPerSheet; i++) {
          const labelIndex = sheet * labelsPerSheet + i;
          if (labelIndex < printJob.labels.length) {
            const label = printJob.labels[labelIndex];
            htmlContent += `
              <div class="label">
                <div class="supplier">SNSM TEXTILES, Salem - 636201</div>
                <div class="client">To, M/s. ${clientDisplayName}</div>
                <div class="pattern">PTTN# ${printJob.item.patternNumber} - ${printJob.item.description}</div>
                <div class="roll-info">
                  <div class="roll-no">Roll No: ${label.roll_number}</div>
                  <div class="qnty">Qnty: ${Number(label.quantity).toFixed(2)} m</div>
                </div>
              </div>
            `;
          } else {
            htmlContent += '<div class="label" style="border: none;"></div>';
          }
        }
        
        htmlContent += '</div></div>';
      }

      htmlContent += '</body></html>';
      
      // Create a blob and open in popup window
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      
      // Open in popup window with specific dimensions
      const popup = window.open(url, 'printWindow', 
        'width=800,height=600,scrollbars=yes,resizable=yes,toolbar=no,menubar=no,location=no,status=no'
      );
      
      if (!popup) {
        // Fallback: create a download link
        const link = document.createElement('a');
        link.href = url;
        link.download = `labels-${printJob.client.name}-${new Date().toISOString().split('T')[0]}.html`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        alert('Labels file downloaded. Open it in your browser and use Ctrl+P to print.');
      } else {
        // Clean up the URL after a delay
        setTimeout(() => {
          URL.revokeObjectURL(url);
        }, 10000);
      }
      
    } catch (error) {
      console.error('PDF generation error:', error);
      alert('Failed to generate labels. Please try again.');
    }
  };

  const generateLegacyA5GatePass = async (printJob) => {
    try {
      const labels = (printJob.labels || []).map(l => ({
        roll: l.roll_number || '',
        qty: parseFloat(l.quantity) || 0
      }));
      const totalMtr = labels.reduce((sum, l) => sum + l.qty, 0);
      const totalRolls = labels.length;
      const gpNo = getAndIncrementGpNumber();
      const poNo = (typeof window !== 'undefined' ? (window.prompt('Enter P.O.No.', '') || '') : '');

      // Layout constants matching sample: 7 column-pairs across, 10 rows per pair
      const rowsPerGroup = 10;
      const groupsPerPage = 7; // each group is two columns: S.No and MTR
      const labelsPerPage = rowsPerGroup * groupsPerPage; // 70
      const totalPages = Math.max(1, Math.ceil(labels.length / labelsPerPage));

      const buildRollNumberSummary = (pageIndex = null) => {
        if (labels.length === 0) return '';
        
        let labelsToProcess = labels;
        if (pageIndex !== null) {
          // For specific page, only process labels for that page
          const start = pageIndex * labelsPerPage;
          labelsToProcess = labels.slice(start, start + labelsPerPage);
        }
        
        const sorted = [...labelsToProcess].map(l => Number(l.roll)).filter(n => !isNaN(n)).sort((a,b) => a - b);
        if (sorted.length === 0) return '';
        const ranges = [];
        let start = sorted[0];
        let prev = sorted[0];
        for (let i = 1; i < sorted.length; i++) {
          const current = sorted[i];
          if (current === prev + 1) {
            prev = current;
            continue;
          }
          ranges.push(start === prev ? String(start) : `${start}-${prev}`);
          start = current;
          prev = current;
        }
        ranges.push(start === prev ? String(start) : `${start}-${prev}`);
        return ranges.join(', ');
      };

      const buildTableForPage = (pageIndex) => {
        const start = pageIndex * labelsPerPage;
        const pageLabels = labels.slice(start, start + labelsPerPage);
        
        // Create 7 groups, each with 10 rows
        const groups = Array.from({ length: groupsPerPage }).map((_, groupIndex) => {
          const gStart = groupIndex * rowsPerGroup;
          const gItems = pageLabels.slice(gStart, gStart + rowsPerGroup);
          const groupTotal = gItems.reduce((sum, it) => sum + (it?.qty || 0), 0);
          return { items: gItems, total: groupTotal };
        });

        // Generate header row with 7 pairs of S.No and MTR columns
        const headerCells = Array.from({ length: groupsPerPage }).map((_, groupIndex) => {
          const hasDataInGroup = groups[groupIndex].items.some(item => item);
          if (hasDataInGroup) {
            return `
              <th>S.No</th>
              <th>MTR</th>
            `;
          }
          return `
            <th style="visibility: hidden;">S.No</th>
            <th style="visibility: hidden;">MTR</th>
          `;
        }).join('');

        // Generate rows only for filled data, but maintain consistent cell sizes
        const filledRows = [];
        for (let rowIndex = 0; rowIndex < rowsPerGroup; rowIndex++) {
          const rowCells = groups.map((group, groupIndex) => {
            const globalIndex = pageIndex * labelsPerPage + groupIndex * rowsPerGroup + rowIndex;
            const serialNumber = globalIndex + 1; // 1-based running S.No
            const item = group.items[rowIndex];
            if (item) {
              return `
                <td class="serial">${serialNumber}</td>
                <td class="mtr">${item.qty.toFixed(2)}</td>
              `;
            }
            return `
              <td class="serial" style="visibility: hidden;">${serialNumber}</td>
              <td class="mtr" style="visibility: hidden;"></td>
            `;
          }).join('');
          
          // Only add row if at least one cell has data
          const hasData = groups.some(group => group.items[rowIndex]);
          if (hasData) {
            filledRows.push(`<tr>${rowCells}</tr>`);
          }
        }
        
        const bodyRows = filledRows.join('');

        // Generate totals row
        const totalsRow = `<tr>
          ${groups.map(g => {
            const hasData = g.items.some(item => item);
            if (hasData) {
              return `
                <td class="total-label">Total</td>
                <td class="total-value">${g.total > 0 ? g.total.toFixed(2) : ''}</td>
              `;
            }
            return `
              <td class="total-label" style="visibility: hidden;">Total</td>
              <td class="total-value" style="visibility: hidden;"></td>
            `;
          }).join('')}
        </tr>`;

        return `
          <table class="gp-table">
            <thead>
              <tr>${headerCells}</tr>
            </thead>
            <tbody>
              ${bodyRows}
              ${totalsRow}
            </tbody>
          </table>
        `;
      };

      const buildPageWithHeaderFooter = (pageIndex) => {
        const tableContent = buildTableForPage(pageIndex);
        return `
          <div class="page-container">
            <div class="header">
              <div class="header-content">
                <div class="sender-info">
                  <div class="sender-name">SNSM TEXTILES</div>
                  <div class="sender-address">3/297D, Ramaiyan Kadu, P.M. Nagar, Seelanaickenpatti,</div>
                  <div class="sender-address">Salem - 636201, Tamil Nadu.</div>
                  <div class="sender-details">GSTIN: 33AHXPS4027M1ZN</div>
                  <div class="sender-details">PH: +91-9940797757</div>
                </div>
                <div class="document-info">
                  <div class="document-title">Goods Packing Slip</div>
                  <div class="document-meta">No: <strong>${gpNo}</strong></div>
                  <div class="document-meta">Date: <strong>${new Date().toLocaleDateString('en-GB')}</strong></div>
                </div>
              </div>
            </div>

            <div class="section">
              <div class="recipient-info">To, M/s. ${printJob.client.name}, ${printJob.client.location || ''}</div>
              <div class="order-details">
                <div>PO No: <strong>${poNo || '-'}</strong></div>
                <div>PTTN# <strong>${printJob.item.patternNumber}</strong> – ${printJob.item.description}</div>
              </div>
            </div>

            <div class="content">
              <div class="table-content">
                ${tableContent}
              </div>

              <div class="footer">
                <div class="summary-info">
                  <div class="summary-item">Total PCS: <strong>${totalRolls}</strong></div>
                  <div class="summary-item">Total MTR: <strong>${totalMtr.toFixed(2)}</strong></div>
                  <div class="summary-item">Roll Nos: <strong>${buildRollNumberSummary(pageIndex)}</strong></div>
                </div>
                <div class="signature-area">
                  <div>For SNSM TEXTILES,</div>
                  <div class="signature-text"> </div>
                </div>
              </div>
            </div>
          </div>
        `;
      };

      const html = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8" />
          <title>Goods Packing Slip - ${printJob.client.name}</title>
          <style>
            @page { size: A5 landscape; margin: 10mm 12mm; }
            body { 
              margin: 0; 
              font-family: Arial, Helvetica, sans-serif; 
              color: #000; 
              font-size: 7pt; 
              line-height: 1.4;
            }
            
            div { display: block; }
            
            .page-container { 
              border: 1px solid #000; 
              margin: 4mm; 
              padding: 6mm; 
              width: 210mm;
              height: 148mm;
              box-sizing: border-box; 
              display: flex;
              flex-direction: column;
              page-break-after: always;
              page-break-inside: avoid;
            }
            
            .header { padding: 3mm 0; border-bottom: 1px solid #000; flex-shrink: 0; }
            .header-content { display: flex; justify-content: space-between; align-items: flex-start; }
            
            .sender-info { flex: 1; }
            .sender-name { 
              font-weight: bold; 
              font-size: 8pt; 
              margin-bottom: 2mm; 
              display: block;
              line-height: 1.4;
            }
            .sender-address { 
              margin-bottom: 1.5mm; 
              font-size: 6pt; 
              display: block;
              line-height: 1.4;
            }
            .sender-details { 
              margin-bottom: 1.5mm; 
              font-size: 6pt; 
              display: block;
              line-height: 1.4;
            }
            
            .document-info { text-align: right; }
            .document-title { 
              font-weight: bold; 
              font-size: 9pt; 
              margin-bottom: 1.5mm; 
              display: block;
              line-height: 1.4;
            }
            .document-meta { 
              margin-bottom: 1mm; 
              font-size: 6pt; 
              display: block;
              line-height: 1.4;
            }
            
            .sender-name, .sender-address, .sender-details, 
            .document-title, .document-meta,
            .recipient-info, .order-details div {
              display: block !important;
              white-space: normal !important;
            }
            
            .section { display: flex; justify-content: space-between; align-items: flex-start; padding: 2mm 0; flex-shrink: 0; }
            .recipient-info { flex: 1; font-size: 6pt; display: block; line-height: 1.4; }
            .order-details { text-align: right; font-size: 6pt; }
            
            .content { margin: 2mm 0; flex: 1; display: flex; flex-direction: column; }
            .table-content { flex: 1; }
            .gp-table { width: 100%; border-collapse: collapse; table-layout: fixed; font-size: 6pt; margin-top: 2mm; }
            .gp-table th, .gp-table td { 
              border: 1px solid #000; 
              padding: 1.5mm 1mm; 
              text-align: center; 
              vertical-align: middle; 
              min-height: 5mm;
              white-space: nowrap;
              line-height: 1.2;
            }
            .gp-table th { font-weight: bold; font-size: 6pt; }
            .gp-table td.serial { text-align: center; }
            .gp-table td.mtr { text-align: right; }
            .gp-table td.total-label { text-align: left; font-weight: bold; }
            .gp-table td.total-value { text-align: right; font-weight: bold; }
            .gp-table th[style*="visibility: hidden"], .gp-table td[style*="visibility: hidden"] { border: none; background: transparent; }
            
            .footer { display: flex; justify-content: space-between; align-items: flex-end; padding: 2mm 0; border-top: 1px solid #000; flex-shrink: 0; margin-top: auto; }
            .summary-info { 
              display: flex; 
              gap: 6mm; 
              font-size: 6pt; 
              line-height: 1.3;
            }
            .summary-item { 
              margin-bottom: 1mm; 
              white-space: nowrap;
            }
            .signature-area { text-align: right; font-size: 5pt; }
            .signature-text { padding: 0; margin: 0; }
            
            .page-container:last-child { page-break-after: avoid; }
            
            @media print {
              /* Enhanced multi-page print fixes with bottom section protection */
              @page {
                size: A5 landscape;
                margin: 5mm 3mm 8mm 3mm !important; /* Top, Right, Bottom, Left - extra bottom margin */
              }
              
              * {
                -webkit-print-color-adjust: exact !important;
                color-adjust: exact !important;
                box-sizing: border-box !important;
              }
              
              html, body {
                width: 210mm !important;
                height: auto !important;
                margin: 0 !important;
                padding: 0 !important;
                font-family: Arial, Helvetica, sans-serif !important;
                font-size: 8pt !important;
                line-height: 1.2 !important;
                overflow: visible !important;
                transform: none !important;
                zoom: 1 !important;
              }
              
              /* Multi-page container fixes */
              .gp-page {
                width: 100% !important;
                height: auto !important;
                overflow: visible !important;
              }
              
              /* Individual page container with bottom section protection */
              .page-container {
                width: 200mm !important;
                height: 130mm !important; /* Reduced height to ensure footer fits */
                margin: 2mm auto !important;
                padding: 5mm !important;
                border: 2px solid #000 !important;
                box-sizing: border-box !important;
                display: flex !important;
                flex-direction: column !important;
                position: relative !important;
                page-break-after: always !important;
                page-break-inside: avoid !important;
                overflow: hidden !important;
                transform: scale(0.95) !important;
                transform-origin: top left !important;
                background: white !important;
              }
              
              .page-container:last-child {
                page-break-after: avoid !important;
              }
              
              /* Ensure consistent header layout across all pages */
              .header {
                padding: 2mm 0 !important;
                border-bottom: 1px solid #000 !important;
                margin-bottom: 2mm !important;
                height: auto !important;
                width: 100% !important;
                flex-shrink: 0 !important;
              }
              
              .header-content {
                display: table !important;
                width: 100% !important;
                table-layout: fixed !important;
              }
              
              .sender-info {
                display: table-cell !important;
                vertical-align: top !important;
                width: 65% !important;
                padding-right: 3mm !important;
              }
              
              .document-info {
                display: table-cell !important;
                vertical-align: top !important;
                width: 35% !important;
                text-align: right !important;
              }
              
              .sender-name {
                font-size: 9pt !important;
                font-weight: bold !important;
                margin-bottom: 1mm !important;
                line-height: 1.1 !important;
              }
              
              .sender-address, .sender-details {
                font-size: 7pt !important;
                margin-bottom: 0.8mm !important;
                line-height: 1.1 !important;
              }
              
              .document-title {
                font-size: 10pt !important;
                font-weight: bold !important;
                margin-bottom: 1mm !important;
                line-height: 1.1 !important;
              }
              
              .document-meta {
                font-size: 7pt !important;
                margin-bottom: 0.5mm !important;
                line-height: 1.1 !important;
              }
              
              /* Section layout for all pages */
              .section {
                display: table !important;
                width: 100% !important;
                table-layout: fixed !important;
                padding: 1.5mm 0 !important;
                margin-bottom: 2mm !important;
                flex-shrink: 0 !important;
              }
              
              .recipient-info {
                display: table-cell !important;
                vertical-align: top !important;
                width: 65% !important;
                font-size: 7pt !important;
                line-height: 1.2 !important;
                padding-right: 3mm !important;
              }
              
              .order-details {
                display: table-cell !important;
                vertical-align: top !important;
                width: 35% !important;
                text-align: right !important;
                font-size: 7pt !important;
                line-height: 1.2 !important;
              }
              
              /* Enhanced table layout with flex grow for content area */
              .content {
                display: flex !important;
                flex-direction: column !important;
                flex: 1 !important;
                height: auto !important;
                margin: 0 !important;
                width: 100% !important;
                min-height: 0 !important;
              }
              
              .table-content {
                display: block !important;
                height: auto !important;
                width: 100% !important;
                flex: 1 !important;
                overflow: visible !important;
              }
              
              .gp-table {
                width: 100% !important;
                border-collapse: collapse !important;
                table-layout: fixed !important;
                font-size: 6pt !important; /* Reduced font size for better fit */
                margin: 0 !important;
                border-spacing: 0 !important;
              }
              
              .gp-table th,
              .gp-table td {
                border: 1px solid #000 !important;
                padding: 0.8mm 0.4mm !important; /* Reduced padding */
                text-align: center !important;
                vertical-align: middle !important;
                font-size: 6pt !important;
                line-height: 1.0 !important;
                min-height: 3mm !important; /* Reduced min-height */
                box-sizing: border-box !important;
                white-space: nowrap !important;
                overflow: hidden !important;
              }
              
              .gp-table th {
                font-weight: bold !important;
                background: transparent !important;
              }
              
              .gp-table td.serial {
                text-align: center !important;
              }
              
              .gp-table td.mtr {
                text-align: right !important;
              }
              
              .gp-table td.total-label {
                text-align: left !important;
                font-weight: bold !important;
              }
              
              .gp-table td.total-value {
                text-align: right !important;
                font-weight: bold !important;
              }
              
              /* Footer with guaranteed bottom positioning */
              .footer {
                display: table !important;
                width: 100% !important;
                table-layout: fixed !important;
                padding: 2mm 0 0 0 !important;
                border-top: 1px solid #000 !important;
                margin-top: auto !important; /* Push to bottom */
                flex-shrink: 0 !important; /* Prevent shrinking */
                position: relative !important;
                bottom: 0 !important;
              }
              
              .summary-info {
                display: table-cell !important;
                vertical-align: bottom !important;
                width: 70% !important;
                font-size: 6pt !important;
                line-height: 1.1 !important;
              }
              
              .summary-item {
                display: inline-block !important;
                margin-right: 4mm !important;
                white-space: nowrap !important;
              }
              
              .signature-area {
                display: table-cell !important;
                vertical-align: bottom !important;
                width: 30% !important;
                text-align: right !important;
                font-size: 5.5pt !important;
                line-height: 1.1 !important;
              }
              
              .signature-text { padding: 0; margin: 0; }
              
              /* Hide empty cells properly */
              .gp-table th[style*="visibility: hidden"],
              .gp-table td[style*="visibility: hidden"] {
                border: none !important;
                background: transparent !important;
                color: transparent !important;
                visibility: hidden !important;
              }
              
              /* Force content visibility and prevent overflow */
              .page-container * {
                max-width: 100% !important;
                overflow: visible !important;
              }
              
              /* Ensure all text elements display properly on all pages */
              .sender-name, .sender-address, .sender-details, 
              .document-title, .document-meta,
              .recipient-info, .order-details div {
                display: block !important;
                white-space: normal !important;
                word-wrap: break-word !important;
              }
              
              /* Additional bottom section protection */
              .page-container::after {
                content: '' !important;
                display: block !important;
                height: 2mm !important;
                width: 100% !important;
                flex-shrink: 0 !important;
              }
            }
          </style>
          <script>
          window.onload = function() { 
            // Enhanced multi-page printing setup with bottom section protection
            document.body.style.zoom = '1';
            document.body.style.transform = 'scale(1)';
            
            // Force all page containers to be properly sized with footer protection
            document.querySelectorAll('.page-container').forEach((container, index) => {
              container.style.transform = 'scale(0.95)';
              container.style.transformOrigin = 'top left';
              container.style.pageBreakAfter = index === container.parentElement.children.length - 1 ? 'avoid' : 'always';
              container.style.pageBreakInside = 'avoid';
              container.style.minHeight = '130mm';
              container.style.maxHeight = '130mm';
              container.style.display = 'flex';
              container.style.flexDirection = 'column';
              container.offsetHeight;
            });
            
            // Force table layout recalculation for all pages
            document.querySelectorAll('.gp-table').forEach(table => {
              table.style.tableLayout = 'fixed';
              table.style.width = '100%';
              table.style.fontSize = '6pt';
              table.offsetHeight;
            });
            
            // Ensure footer positioning on all pages
            document.querySelectorAll('.footer').forEach(footer => {
              footer.style.marginTop = 'auto';
              footer.style.flexShrink = '0';
              footer.style.position = 'relative';
              footer.style.bottom = '0';
              footer.offsetHeight;
            });
            
            // Ensure all pages are rendered before printing
            setTimeout(() => {
              // Final layout check with footer verification
              document.body.offsetHeight;
              console.log('Total pages to print:', document.querySelectorAll('.page-container').length);
              console.log('Footer elements found:', document.querySelectorAll('.footer').length);
              window.print();
            }, 3500); // Increased to 3500ms for footer positioning
          };
          </script>
        </head>
        <body>
          <div class="gp-page">
            ${Array.from({ length: totalPages }).map((_, pageIndex) => 
              buildPageWithHeaderFooter(pageIndex)
            ).join('')}
          </div>
        </body>
        </html>
      `;

      // Generate HTML (removed PDF functionality)
      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const popup = window.open(url, 'gpWindow', 'width=900,height=700,scrollbars=yes,resizable=yes');
      if (!popup) {
        const link = document.createElement('a');
        link.href = url;
        link.download = `gate-pass-${printJob.client.name}-${new Date().toISOString().split('T')[0]}.html`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        alert('Gate Pass file downloaded. Open it and print.');
      } else {
        setTimeout(() => URL.revokeObjectURL(url), 10000);
      }
    } catch (error) {
      console.error('Gate pass generation error:', error);
      alert('Failed to generate gate pass.');
    }
  };



  // Legacy filter UI removed

  // Show history for client or item
  const showHistory = (type, data) => {
    let filteredData = [];
    let title = '';
    let historyType = '';
    
    if (type === 'client') {
      filteredData = printJobs.filter(job => 
        job.client.name === data.name
      );
      title = `History for Client: ${data.name}`;
      historyType = 'client';
    } else if (type === 'item') {
      filteredData = printJobs.filter(job => 
        job.item.patternNumber === data.pattern_number
      );
      title = `History for Item: #${data.pattern_number} - ${data.description}`;
      historyType = 'item';
    }
    
    setHistoryData(filteredData);
    setHistoryTitle(title);
    setHistoryType(historyType);
    setShowHistoryModal(true);
  };

  // Legacy manual report generator removed (auto-updates via useEffect)

  // Auto-generate report results on selection
  useEffect(() => {
    if (!reportType) {
      setReportHistoryData([]);
      return;
    }
    if (reportType === 'all') {
      setReportHistoryData(printJobs);
      return;
    }
    if (!selectedReportItem) {
      setReportHistoryData([]);
      return;
    }
    if (reportType === 'item') {
      setReportHistoryData(printJobs.filter(job => job.item.patternNumber === selectedReportItem.pattern_number));
      return;
    }
    if (reportType === 'user') {
      setReportHistoryData(printJobs.filter(job => job.client.name === selectedReportItem.name));
      return;
    }
  }, [reportType, selectedReportItem, printJobs]);

  // Reset report selections
  const resetReportSelections = () => {
    setReportType('');
    setSelectedReportItem(null);
    setReportHistoryData([]);
  };

  

  // Update settings
  const updateSettings = async (newSettings) => {
    try {
      await Promise.all([
        axios.put('/api/settings', {
          setting_key: 'starting_roll_number',
          setting_value: newSettings.startingRollNumber.toString()
        }),
        axios.put('/api/settings', {
          setting_key: 'current_roll_number',
          setting_value: newSettings.currentRollNumber.toString()
        })
      ]);
      
      setGlobalSettings(newSettings);
    } catch (error) {
      console.error('Error updating settings:', error);
      alert('Failed to update settings');
    }
  };

  // Component renders

  const setShowHistoryModalForJob = (job) => {
    setHistoryTitle(`Job Details: ${job.client.name} / #${job.item.patternNumber}`);
    setHistoryType('job');
    setHistoryData([job]);
    setShowHistoryModal(true);
  };
  const ClientForm = ({ client, onSave, onCancel }) => {
    const [formData, setFormData] = useState(client || { name: '', location: '', code: '' });

    return (
      <div className="modal-overlay">
        <div className="modal">
          <div className="modal-header">
            <h3 className="modal-title">{client ? 'Edit Client' : 'Add Client'}</h3>
            <button onClick={onCancel} className="modal-close">×</button>
          </div>
          
          <div className="form-group">
            <label className="form-label">Client Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="form-input"
              placeholder="e.g., A.B.L.B.L"
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Location</label>
            <input
              type="text"
              value={formData.location}
              onChange={(e) => setFormData({...formData, location: e.target.value})}
              className="form-input"
              placeholder="e.g., BLR"
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Nick name</label>
            <input
              type="text"
              value={formData.code}
              onChange={(e) => setFormData({...formData, code: e.target.value})}
              className="form-input"
              placeholder="e.g., Nick"
            />
          </div>
          
          <div className="modal-actions">
            <button
              onClick={() => onSave(formData)}
              className="btn btn-primary"
            >
              Save
            </button>
            <button
              onClick={onCancel}
              className="btn btn-secondary"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  };

  const ItemForm = ({ item, onSave, onCancel }) => {
    const [formData, setFormData] = useState(item || { pattern_number: '', description: '', quality: '', net_weight: '', gross_weight: '' });

    return (
      <div className="modal-overlay">
        <div className="modal">
          <div className="modal-header">
            <h3 className="modal-title">{item ? 'Edit Item' : 'Add Item'}</h3>
            <button onClick={onCancel} className="modal-close">×</button>
          </div>
          
          <div className="form-group">
            <label className="form-label">Pattern Number</label>
            <input
              type="text"
              value={formData.pattern_number}
              onChange={(e) => setFormData({...formData, pattern_number: e.target.value})}
              className="form-input"
              placeholder="e.g., FYSDSH1835E"
              required
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Description/Color</label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="form-input"
              placeholder="e.g., Pastel Yellow"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Quality</label>
            <input
              type="text"
              value={formData.quality}
              onChange={(e) => setFormData({...formData, quality: e.target.value})}
              className="form-input"
              placeholder="e.g., Cotton"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Net Weight (kg)</label>
            <input
              type="number"
              step="0.01"
              value={formData.net_weight || ''}
              onChange={(e) => setFormData({ ...formData, net_weight: e.target.value })}
              className="form-input"
              placeholder="e.g., 1.25"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Gross Weight (kg)</label>
            <input
              type="number"
              step="0.01"
              value={formData.gross_weight || ''}
              onChange={(e) => setFormData({ ...formData, gross_weight: e.target.value })}
              className="form-input"
              placeholder="e.g., 1.40"
              required
            />
          </div>
          
          <div className="modal-actions">
            <button
              onClick={() => {
                if (!formData.pattern_number?.trim() || !formData.description?.trim()) {
                  alert('Pattern Number and Description are required');
                  return;
                }
                if (formData.net_weight === '' || formData.gross_weight === '') {
                  alert('Net Weight and Gross Weight are required');
                  return;
                }
                const net = parseFloat(formData.net_weight);
                const gross = parseFloat(formData.gross_weight);
                if (Number.isNaN(net) || Number.isNaN(gross)) {
                  alert('Net Weight and Gross Weight must be valid numbers');
                  return;
                }
                onSave({
                  ...formData,
                  net_weight: net,
                  gross_weight: gross,
                });
              }}
              className="btn btn-primary"
            >
              Save
            </button>
            <button
              onClick={onCancel}
              className="btn btn-secondary"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="label-printing-system">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="label-printing-system">
      {/* Header */}
      <div className="system-header">
        <h1 className="system-title">SNSM Label Printing System v2.0</h1>
        <div className="current-roll">
          Current Roll Number: {globalSettings.currentRollNumber}
        </div>
      </div>

      {/* Navigation */}
      <div className="nav-tabs">
        {[
          { id: 'print', label: 'Print Labels', icon: Printer },
          { id: 'clients', label: 'Client Master', icon: Edit2 },
          { id: 'items', label: 'Item Master', icon: FileText },
          { id: 'history', label: 'History', icon: Eye },
          { id: 'settings', label: 'Settings', icon: Settings }
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`nav-tab ${activeTab === id ? 'active' : ''}`}
          >
            <Icon size={16} />
            {label}
          </button>
        ))}
      </div>

      {/* Main Content */}
      <div className="content-container">
        {activeTab === 'print' && (
          <div>
            <h2 className="section-title">Create Print Job</h2>
            
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Select Client</label>
                <select
                  value={currentPrintJob.clientId}
                  onChange={(e) => handlePrintJobChange('clientId', e.target.value)}
                  className="form-select"
                >
                  <option value="">Select a client...</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>
                      {client.name} - {client.location} ({client.code})
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Select Item</label>
                <select
                  value={currentPrintJob.itemId}
                  onChange={(e) => handlePrintJobChange('itemId', e.target.value)}
                  className="form-select"
                >
                  <option value="">Select an item...</option>
                  {items.map(item => (
                    <option key={item.id} value={item.id}>
                      {item.pattern_number} - {item.description}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Number of Rolls</label>
                <input
                  type="number"
                  min="1"
                  value={currentPrintJob.numberOfRolls}
                  onChange={(e) => handlePrintJobChange('numberOfRolls', e.target.value)}
                  className="form-input"
                />
              </div>
            </div>

            {currentPrintJob.numberOfRolls > 0 && (
              <div>
                <h3 className="section-title">Enter MTR Values for Each Roll</h3>
                {/* Quick summary for user friendliness */}
                <div className="mtr-summary">
                  {(() => {
                    const numRolls = parseInt(currentPrintJob.numberOfRolls) || 0;
                    const startRoll = globalSettings.currentRollNumber;
                    const endRoll = numRolls > 0 ? startRoll + numRolls - 1 : startRoll;
                    const filled = (currentPrintJob.mtrValues || []).filter(v => v !== '' && v !== null && v !== undefined).length;
                    const totalMtr = (currentPrintJob.mtrValues || []).reduce((sum, v) => sum + (parseFloat(v) || 0), 0).toFixed(2);
                    return (
                      <div className="summary-row">
                        <span className="summary-chip">Rolls: {numRolls}</span>
                        <span className="summary-chip">Filled: {filled}/{numRolls}</span>
                        <span className="summary-chip">Roll Nos: {startRoll} – {endRoll}</span>
                        <span className="summary-chip">Total MTR: {totalMtr}</span>
                      </div>
                    );
                  })()}
                </div>
                <div className="table-container">
                  <table className="mtr-table">
                    <thead>
                      <tr>
                        <th>Select</th>
                        <th>Roll Number</th>
                        <th>MTR</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array(parseInt(currentPrintJob.numberOfRolls) || 0).fill().map((_, index) => (
                        <tr key={index}>
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedMTRRows.has(index)}
                              onChange={() => handleMTRRowSelection(index)}
                              className="checkbox-input"
                            />
                          </td>
                          <td>Roll {globalSettings.currentRollNumber + index}</td>
                          <td>
                            <input
                              type="text"
                              value={currentPrintJob.mtrValues[index] || ''}
                              onChange={(e) => handleMTRChange(index, e.target.value)}
                              className="mtr-input"
                              placeholder="Enter MTR (e.g., 10.50)"
                            />
                          </td>
                          <td>
                            <div className="row-actions">
                              <button
                                onClick={() => insertRow(index)}
                                className="btn-icon btn-insert"
                                title="Insert row above"
                              >
                                <Plus size={14} />
                              </button>
                              <button
                                onClick={() => deleteRow(index)}
                                className="btn-icon btn-delete"
                                title="Delete this row"
                                disabled={(parseInt(currentPrintJob.numberOfRolls) || 0) <= 1}
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="form-actions">
                  <div className="mtr-controls">
                    <div className="select-all-container">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={selectAllMTRRows}
                          onChange={handleSelectAllMTRRows}
                          className="checkbox-input"
                        />
                        <span className="checkbox-text">Select All Rows</span>
                      </label>
                    </div>
                    <button
                      onClick={deleteSelectedMTRRows}
                      disabled={selectedMTRRows.size === 0}
                      className="btn btn-danger"
                    >
                      <Trash2 size={16} />
                      Delete Selected ({selectedMTRRows.size})
                    </button>
                  </div>
                  <div className="add-remove-controls">
                    <button
                      onClick={() => handlePrintJobChange('numberOfRolls', (parseInt(currentPrintJob.numberOfRolls) || 0) + 1)}
                      className="btn btn-secondary"
                    >
                      <Plus size={16} />
                      Add Another Roll
                    </button>
                    <button
                      onClick={removeLastRoll}
                      className="btn btn-secondary"
                      disabled={(parseInt(currentPrintJob.numberOfRolls) || 0) <= 1}
                    >
                      <Minus size={16} />
                      Remove Last Roll
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div>
              <button
                onClick={generatePrintJob}
                disabled={!currentPrintJob.clientId || !currentPrintJob.itemId || currentPrintJob.mtrValues.some(mtr => !mtr)}
                className="btn btn-primary"
              >
                <Printer size={16} />
                Generate & Print Labels
              </button>
            </div>
          </div>
        )}

        {activeTab === 'clients' && (
          <div>
            <div className="form-row">
              <h2 className="section-title">Client Master</h2>
              <button
                onClick={() => setShowAddClient(true)}
                className="btn btn-primary"
              >
                <Plus size={16} />
                Add Client
              </button>
            </div>

            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Location</th>
                    <th>Nick name</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {clients.map(client => (
                    <tr key={client.id}>
                      <td className="clickable-cell" onClick={() => showHistory('client', client)}>{client.name}</td>
                      <td className="clickable-cell" onClick={() => showHistory('client', client)}>{client.location}</td>
                      <td className="clickable-cell" onClick={() => showHistory('client', client)}>{client.code}</td>
                      <td>
                        <div className="action-buttons">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingClient(client);
                            }}
                            className="btn-icon btn-edit"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteClient(client.id);
                            }}
                            className="btn-icon btn-delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'items' && (
          <div>
            <div className="form-row">
              <h2 className="section-title">Item Master</h2>
              <button
                onClick={() => setShowAddItem(true)}
                className="btn btn-primary"
              >
                <Plus size={16} />
                Add Item
              </button>
            </div>

            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Pattern Number</th>
                    <th>Description</th>
                    <th>Quality</th>
                    <th>Net Wt</th>
                    <th>Gross Wt</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(item => (
                    <tr key={item.id}>
                      <td className="clickable-cell" onClick={() => showHistory('item', item)}>{item.pattern_number}</td>
                      <td className="clickable-cell" onClick={() => showHistory('item', item)}>{item.description}</td>
                      <td className="clickable-cell" onClick={() => showHistory('item', item)}>{item.quality || '—'}</td>
                      <td className="clickable-cell" onClick={() => showHistory('item', item)}>{item.net_weight != null ? Number(item.net_weight).toFixed(2) : '—'}</td>
                      <td className="clickable-cell" onClick={() => showHistory('item', item)}>{item.gross_weight != null ? Number(item.gross_weight).toFixed(2) : '—'}</td>
                      <td>
                        <div className="action-buttons">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingItem(item);
                            }}
                            className="btn-icon btn-edit"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteItem(item.id);
                            }}
                            className="btn-icon btn-delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'history' && (
          <div>
            <h2 className="section-title">History</h2>
            
            {/* Roll Number Search */}
            <div className="search-section">
              <div className="search-container">
                <div className="search-input-group">
                  <input
                    type="text"
                    value={rollSearchQuery}
                    onChange={(e) => setRollSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && searchRollNumber()}
                    className="search-input"
                    placeholder="Search by roll number (e.g., 1001, 1005)"
                  />
                  <button
                    onClick={searchRollNumber}
                    className="btn btn-primary search-btn"
                    disabled={!rollSearchQuery.trim() || isSearching}
                  >
                    {isSearching ? (
                      <div className="loading" style={{ width: '16px', height: '16px' }}></div>
                    ) : (
                      <Search size={16} />
                    )}
                    {isSearching ? 'Searching...' : 'Search'}
                  </button>
                  {showSearchResults && (
                    <button
                      onClick={clearSearch}
                      className="btn btn-secondary clear-btn"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Search Results */}
            {showSearchResults && (
              <div className="search-results-section">
                <h3 className="section-subtitle">
                  Search Results for Roll Number: "{rollSearchQuery}"
                  {searchResults.length > 0 && ` (${searchResults.length} found)`}
                </h3>
                
                {searchResults.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-state-icon">🔍</div>
                    <div className="empty-state-title">No Results Found</div>
                    <div className="empty-state-text">
                      No roll numbers found matching "{rollSearchQuery}". Try a different search term.
                    </div>
                  </div>
                ) : (
                  <div className="table-container">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Roll Number</th>
                          <th>Client Name</th>
                          <th>Item Name</th>
                          <th>MTR</th>
                          <th>Date</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {searchResults.map((result, index) => (
                          <tr key={index}>
                            <td className="roll-number-cell">{result.rollNumber}</td>
                            <td className="clickable-cell" onClick={() => setShowHistoryModalForJob(result.job)}>
                              {result.clientName}
                            </td>
                            <td className="clickable-cell" onClick={() => setShowHistoryModalForJob(result.job)}>
                              {result.itemName}
                            </td>
                            <td className="clickable-cell" onClick={() => setShowHistoryModalForJob(result.job)}>
                              {formatNumber(result.mtr)}
                            </td>
                            <td className="clickable-cell" onClick={() => setShowHistoryModalForJob(result.job)}>
                              {result.date}
                            </td>
                            <td>
                              <div className="action-buttons">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setShowHistoryModalForJob(result.job);
                                  }}
                                  className="btn-icon btn-edit"
                                  title="View Job Details"
                                >
                                  <Eye size={14} />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    generateGatePass(result.job);
                                  }}
                                  className="btn-icon btn-secondary"
                                  title="Print GP"
                                >
                                  <FileText size={14} />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    generatePDF(result.job);
                                  }}
                                  className="btn-icon btn-secondary"
                                  title="Print Labels"
                                >
                                  <Printer size={14} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
            
            {/* Step 1: Select Report Type */}
            <div className="report-section">
              <div className="report-type-selection">
                <button
                  onClick={() => setReportType('item')}
                  className={`report-type-btn ${reportType === 'item' ? 'active' : ''}`}
                >
                  <FileText size={20} />
                  Item-wise History
                </button>
                <button
                  onClick={() => setReportType('user')}
                  className={`report-type-btn ${reportType === 'user' ? 'active' : ''}`}
                >
                  <Edit2 size={20} />
                  Client-wise History
                </button>
              </div>
            </div>

            {/* Step 2: Select Item or User */}
            {reportType && (
              <div className="report-section">
                <h3 className="section-subtitle">
                  Step 2: Select {reportType === 'item' ? 'Item' : 'User'}
                </h3>
                <div className="form-group">
                  <label className="form-label">
                    Choose {reportType === 'item' ? 'an item' : 'a user'} to view history:
                  </label>
                  <select
                    value={selectedReportItem ? (reportType === 'item' ? selectedReportItem.id : selectedReportItem.id) : ''}
                    onChange={(e) => {
                      const selectedId = parseInt(e.target.value);
                      if (reportType === 'item') {
                        const selectedItem = items.find(item => item.id === selectedId);
                        setSelectedReportItem(selectedItem);
                      } else {
                        const selectedClient = clients.find(client => client.id === selectedId);
                        setSelectedReportItem(selectedClient);
                      }
                    }}
                    className="form-select"
                  >
                    <option value="">Select {reportType === 'item' ? 'an item' : 'a user'}...</option>
                    {reportType === 'item' 
                      ? items.map(item => (
                          <option key={item.id} value={item.id}>
                            #{item.pattern_number} - {item.description}
                          </option>
                        ))
                      : clients.map(client => (
                          <option key={client.id} value={client.id}>
                            {client.name} - {client.location} ({client.code})
                          </option>
                        ))
                    }
                  </select>
                </div>
                
                <div className="report-actions">
                  <button
                    onClick={resetReportSelections}
                    className="btn btn-secondary"
                  >
                    Reset Selection
                  </button>
                </div>
              </div>
            )}

            {/* Report Results */}
            {reportHistoryData.length > 0 && (
              <div className="report-section">
                <h3 className="section-subtitle">Report Results</h3>
                
                {/* Collated GP Print Controls for Report Results */}
                <div className="collated-controls">
                  <div className="select-all-container">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={selectAllJobs}
                        onChange={handleSelectAllJobs}
                        className="checkbox-input"
                      />
                      <span className="checkbox-text">Select All Jobs</span>
                    </label>
                  </div>
                  <button
                    onClick={generateCollatedGatePass}
                    disabled={selectedJobs.size === 0}
                    className="btn btn-primary"
                  >
                    <FileText size={16} />
                    Print Collated GP ({selectedJobs.size} selected)
                  </button>
                </div>
                
                <div className="history-log-container">
                  {(() => {
                    // Group report data by date like history log
                    const dateGroups = new Map();
                    reportHistoryData.forEach(job => {
                      const date = formatDate(job.timestamp);
                      const clientName = job.client.name;
                      const itemName = `${job.item.patternNumber} - ${job.item.description}`;
                      const totalRolls = job.labels.length;
                      const totalMtr = job.labels.reduce((sum, label) => sum + (parseFloat(label.quantity) || 0), 0);
                      
                      if (!dateGroups.has(date)) {
                        dateGroups.set(date, []);
                      }
                      dateGroups.get(date).push({ id: job.id, job, client: clientName, item: itemName, totalRolls, totalMtr });
                    });
                    
                    const groupedData = Array.from(dateGroups.entries())
                      .map(([date, entries]) => ({ date, entries }))
                      .sort((a, b) => new Date(b.date.split('/').reverse().join('-')) - new Date(a.date.split('/').reverse().join('-')));
                    
                    return groupedData.map((dateGroup, index) => (
                      <div key={index} className="date-group">
                        <div className="date-header">{dateGroup.date}</div>
                        <div className="table-container">
                          <table className="table">
                            <thead>
                              <tr>
                                <th>Select</th>
                                {reportType === 'item' && <th>Client Name</th>}
                                {reportType === 'user' && <th>Item Name</th>}
                                <th>Total Rolls</th>
                                <th>Total MTR</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {dateGroup.entries.map((entry, entryIndex) => (
                                <tr key={entryIndex}>
                                  <td>
                                    <input
                                      type="checkbox"
                                      checked={selectedJobs.has(entry.job.id)}
                                      onChange={() => handleJobSelection(entry.job.id)}
                                      className="checkbox-input"
                                    />
                                  </td>
                                  {reportType === 'item' && <td className="clickable-cell" onClick={() => setShowHistoryModalForJob(entry.job)}>{entry.client}</td>}
                                  {reportType === 'user' && <td className="clickable-cell" onClick={() => setShowHistoryModalForJob(entry.job)}>{entry.item}</td>}
                                  <td className="clickable-cell" onClick={() => setShowHistoryModalForJob(entry.job)}>{entry.totalRolls}</td>
                                  <td className="clickable-cell" onClick={() => setShowHistoryModalForJob(entry.job)}>{formatNumber(entry.totalMtr)}</td>
                                  <td>
                                    <div className="action-buttons">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          generateGatePass(entry.job);
                                        }}
                                        className="btn-icon btn-edit"
                                        title="Print GP"
                                      >
                                        <FileText size={14} />
                                      </button>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          generatePDF(entry.job);
                                        }}
                                        className="btn-icon btn-secondary"
                                        title="Print Labels"
                                      >
                                        <Printer size={14} />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ));
                  })()}
                </div>
                
                <div className="report-summary">
                  <p>Total records found: {reportHistoryData.length}</p>
                </div>
              </div>
            )}


            {/* Moved History Log here */}
            <div className="report-section">
              <h3 className="section-subtitle">History Log</h3>
              {historyLog.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-state-icon">📊</div>
                  <div className="empty-state-title">No History Data</div>
                  <div className="empty-state-text">
                    Start printing labels to see your history log here.
                  </div>
                </div>
              ) : (
                <div className="history-log-container">
                  {/* Collated GP Print Controls */}
                  <div className="collated-controls">
                    <div className="select-all-container">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={selectAllJobs}
                          onChange={handleSelectAllJobs}
                          className="checkbox-input"
                        />
                        <span className="checkbox-text">Select All Jobs</span>
                      </label>
                    </div>
                    <button
                      onClick={generateCollatedGatePass}
                      disabled={selectedJobs.size === 0}
                      className="btn btn-primary"
                    >
                      <FileText size={16} />
                      Print Collated GP ({selectedJobs.size} selected)
                    </button>
                  </div>
                  
                  {historyLog.map((dateGroup, index) => (
                    <div key={index} className="date-group">
                      <div className="date-header">{dateGroup.date}</div>
                      <div className="table-container">
                        <table className="table">
                          <thead>
                            <tr>
                              <th>Select</th>
                              <th>Client Name</th>
                              <th>Item Name</th>
                              <th>Total Rolls</th>
                              <th>Total MTR</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {dateGroup.entries.map((entry, entryIndex) => (
                              <tr key={entryIndex}>
                                <td>
                                  <input
                                    type="checkbox"
                                    checked={selectedJobs.has(entry.job.id)}
                                    onChange={() => handleJobSelection(entry.job.id)}
                                    className="checkbox-input"
                                  />
                                </td>
                                <td className="clickable-cell" onClick={() => setShowHistoryModalForJob(entry.job)}>{entry.client}</td>
                                <td className="clickable-cell" onClick={() => setShowHistoryModalForJob(entry.job)}>{entry.item}</td>
                                <td className="clickable-cell" onClick={() => setShowHistoryModalForJob(entry.job)}>{entry.totalRolls}</td>
                                <td className="clickable-cell" onClick={() => setShowHistoryModalForJob(entry.job)}>{formatNumber(entry.totalMtr)}</td>
                                <td>
                                  <div className="action-buttons">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        generateGatePass(entry.job);
                                      }}
                                      className="btn-icon btn-edit"
                                      title="Print GP"
                                    >
                                      <FileText size={14} />
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        generatePDF(entry.job);
                                      }}
                                      className="btn-icon btn-secondary"
                                      title="Print Labels"
                                    >
                                      <Printer size={14} />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div>
            <h2 className="section-title">System Settings</h2>
            
            <div className="form-grid">
              <div className="card">
                <div className="form-group">
                  <label className="form-label">Starting Roll Number</label>
                  <input
                    type="number"
                    value={globalSettings.startingRollNumber}
                    onChange={(e) => updateSettings({
                      ...globalSettings,
                      startingRollNumber: parseInt(e.target.value)
                    })}
                    className="form-input"
                  />
                  <p className="form-help">
                    This is the initial roll number when the system starts.
                  </p>
                </div>
              </div>

              <div className="card">
                <div className="form-group">
                  <label className="form-label">Current Roll Number</label>
                  <input
                    type="number"
                    value={globalSettings.currentRollNumber}
                    onChange={(e) => updateSettings({
                      ...globalSettings,
                      currentRollNumber: parseInt(e.target.value)
                    })}
                    className="form-input"
                  />
                  <p className="form-help">
                    This is the next roll number that will be assigned.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <button
                onClick={() => updateSettings({
                  ...globalSettings,
                  currentRollNumber: globalSettings.startingRollNumber
                })}
                className="btn btn-danger"
              >
                Reset Current Roll Number
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showAddClient && (
        <ClientForm
          onSave={addClient}
          onCancel={() => setShowAddClient(false)}
        />
      )}

      {editingClient && (
        <ClientForm
          client={editingClient}
          onSave={(data) => updateClient(editingClient.id, data)}
          onCancel={() => setEditingClient(null)}
        />
      )}

      {showAddItem && (
        <ItemForm
          onSave={addItem}
          onCancel={() => setShowAddItem(false)}
        />
      )}

      {editingItem && (
        <ItemForm
          item={editingItem}
          onSave={(data) => updateItem(editingItem.id, data)}
          onCancel={() => setEditingItem(null)}
        />
      )}

      {/* History Modal */}
      {showHistoryModal && (
        <div className="modal-overlay">
          <div className="modal history-modal">
            <div className="modal-header">
              <h3 className="modal-title">{historyTitle}</h3>
              <button onClick={() => setShowHistoryModal(false)} className="modal-close">×</button>
            </div>
            
            <div className="table-container">
              {historyData.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-state-icon">📊</div>
                  <div className="empty-state-title">No History Found</div>
                  <div className="empty-state-text">No print jobs found for this selection.</div>
                </div>
              ) : (
                <table className="table">
                  <thead>
                    <tr>
                      {historyType === 'item' && <th>Client</th>}
                      {historyType === 'client' && <th>Item</th>}
                      <th>Roll Number</th>
                      <th>MTR</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyData.flatMap(job => 
                      job.labels.length > 0 
                        ? job.labels.map((label, labelIndex) => (
                            <tr key={`${job.id}-${label.roll_number}-${labelIndex}`}>
                              {historyType === 'item' && <td>{job.client.name}</td>}
                              {historyType === 'client' && <td>#{job.item.patternNumber} - {job.item.description}</td>}
                              <td>{label.roll_number || 'N/A'}</td>
                              <td>{formatNumber(label.quantity)}</td>
                            </tr>
                          ))
                        : [(
                            <tr key={`${job.id}-no-labels`}>
                              {historyType === 'item' && <td>{job.client.name}</td>}
                              {historyType === 'client' && <td>#{job.item.patternNumber} - {job.item.description}</td>}
                              <td>N/A</td>
                              <td>N/A</td>
                            </tr>
                          )]
                    )}
                  </tbody>
                </table>
              )}
            </div>
            
            {historyData.length > 0 && (
              <div className="report-summary">
                {(() => {
                  const totalRolls = historyData.reduce((count, j) => count + (j.labels?.length || 0), 0);
                  const totalMtr = historyData.reduce((sum, j) => sum + (j.labels || []).reduce((s, l) => s + (parseFloat(l.quantity) || 0), 0), 0);
                  return <p>Total Rolls: {totalRolls} | Total MTR: {totalMtr.toFixed(2)}</p>;
                })()}
              </div>
            )}

            <div className="modal-actions" style={{justifyContent: 'space-between'}}>
              <div>
                {historyData.length === 1 && (
                  <>
                    <button className="btn btn-secondary" onClick={() => generateGatePass(historyData[0])}>
                      <Printer size={16} /> GP
                    </button>
                    <button className="btn btn-secondary" onClick={() => generatePDF(historyData[0])}>
                      <Printer size={16} /> Label
                    </button>
                  </>
                )}
              </div>
              <button
                onClick={() => setShowHistoryModal(false)}
                className="btn btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LabelPrintingSystem;